import { deviceSession } from '@/privacy/deviceStorage';
import { workAreaDisplayName } from '@featit/shared/work-area-seed-names';
import {
  Suspense,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { accentIconProps } from '@/components/accentIcon';
import { installAccentWalks } from '@/components/accentWalk';
import { RefusalDescription, useRefusalNote } from '@/components/RefusalNote';
import {
  Link,
  NavLink,
  Outlet,
  matchPath,
  useLocation,
  useMatches,
  useNavigate,
} from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { SidebarNewPageButton } from './SidebarNewPageButton';
import {
  workAreaRoutePath,
  appPath,
  parseAddress,
  appRouteOf,
  workAreaReferenceOf,
} from '@featit/shared/addressing/paths';
import {
  workAreaQualifiedLabel,
  workAreaRouteRef,
} from '@featit/shared/addressing/workAreaRouteRef';
import { barTitleOf, type IBarTitleOptions } from '@featit/shared/bar-title';
import { chromeLabelText, type ChromeTranslateFn } from '@featit/shared/chrome-label';
import { isGuestWorkArea, memberWorkAreas } from '@featit/shared/guest-work-area';
import { SHELL_ALL_WORK_AREAS_SCOPE, SHELL_WORK_AREA_EDIT_PATH } from '@featit/shared/shell-paths';
import { type IProject } from '@featit/shared/types/index';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableArea, useRowSortable, useSortableKit } from './lazySortable';
import {
  ChevronDown,
  ChevronRight,
  CloudOff,
  GripVertical,
  LockKeyhole,
  LogIn,
  LogOut,
  Menu,
  PanelLeftOpen,
  RotateCw,
  Plus,
  X,
  Building2,
  MessageSquarePlus,
} from 'lucide-react';
import { holdKeyboardForDialog } from '@/hooks/useDialogFocusTrap';
import { useStores } from '@/hooks/useStores';
import { dismissSignOut, requestSignOut, signOutPrompt } from '@/services/signOutPrompt';
import { PinnedSidebar } from './PinnedSidebar';
import { KeyboardHoldFallback } from './KeyboardHoldFallback';
import { useCommittedRouteLocation } from '@/routing/committedRouteLocation';
import { sharedSwitcherOrder } from '@/features/settings/usePhonePreferences';
import {
  CreateNamespaceRow,
  EmptyNamespaceLine,
  NamespaceTreeStatus,
} from './WorkAreaNamespaceTree';
import { WordTruncate } from './WordTruncate';
import { WorkAreaPickerOptions } from './WorkAreaPickerOptionsChunk';
import { onHoverPointer } from '@/hooks/hoverPointer';
import { afterBootRequests } from '@/services/bootRequests';
import {
  groupWorkAreasByNamespace,
  groupsWithWorkAreas,
  type IWorkAreaNamespaceGroup,
} from './workAreaNamespaceGroups';
import { workAreaRowIndent } from './workAreaIndent';
import { AnchoredPortal, useAnchoredStyle } from './AnchoredPortal';
import { useDismissableLayer } from './useDismissable';
import { SidebarProfileLink } from './SidebarProfileLink';
import { Button } from './Button';
import { FeatItIcon } from './FeatItIcon';
import { useOneSidebarScroller } from './useOneSidebarScroller';
import { BottomScrollFade, useBottomScrollFade } from './BottomScrollFade';
import { ThemeToggle } from './ThemeToggle';
import { noticesWaitingInScope, withNotices } from '@/features/notifications/noticesInScope';
import {
  DeploymentUpdateHeaderSlot,
  DeploymentUpdateRow,
  preloadDeploymentUpdateParts,
  deploymentUpdateScopeLine,
  useDeploymentUpdate,
} from '@/components/DeploymentUpdate';
import { DesktopDownloadButton } from '@/features/desktop-download/DesktopDownloadButton';
import { DecisionAnswerToastSlot } from '@/features/decisions/DecisionAnswerToastSlot';
import { ItemArchiveToastSlot } from '@/features/work-items/ItemArchiveToastSlot';
import { ApprovalAnswerToastSlot } from '@/features/dashboard/ApprovalAnswerToastSlot';
import { ItemCreatedToastSlot } from '@/features/work-items/ItemCreatedToastSlot';
import { ExampleWorkAreaBannerSlot } from '@/features/examples/ExampleWorkAreaBannerSlot';
import { InvitationJoinedNotice } from '@/features/access/InvitationJoinedNotice';
import { passkeyOfferPending } from '@/features/access/oneTapEntry';
import { InvitationWaitingNotice } from './InvitationWaitingNotice';
import { signUpInvitationLanding } from '@/features/access/signUpInvitationLanding';
import { GlobalSearchBar } from '@/features/search/GlobalSearchBar';
import { ViewSearchBarContext, ViewSearchScopes } from '@/features/search/viewSearchScopes';
import { focusGlobalSearch, openSearchBar } from '@/features/search/focusGlobalSearch';
import { PhoneDensityAttribute } from '@/styles/usePhoneDensityAttribute';
import { VerificationBanner } from '@/features/auth/VerificationBanner';
import { ShellBottomBarSlot, VIEWPORT_REMAINDER, ViewportTitleBar } from '@/components/AppViewport';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { projectHomePath, projectNewItemPath } from '@/features/work-items/projectItemUrls';
import {
  moveNamespaceWithinParent,
  moveWorkAreaWithinParent,
  type WorkAreaHierarchyRow,
} from '@/features/work-areas/workAreaHierarchy';
import {
  MoveArrows,
  namespaceDragAnnouncements,
  namespaceMoveTarget,
  SortableNamespaceHeading,
  workAreaMoveTarget,
  type NamespaceMoveDirection,
} from './NamespaceReorder';
import { SidebarActionsMenu } from './SidebarActionsMenu';
import {
  DESKTOP_LAYOUT_MEDIA_QUERY,
  LG_MEDIA_QUERY,
  TOUCH_ONLY_MEDIA_QUERY,
  useMediaQuery,
  useTouchDesktopLayout,
} from '@/hooks/useMediaQuery';
import { useDrawerMotion } from './sheetMotion';
import {
  useResizableBoxHeight,
  type IResizableBoxHeight,
} from '@/features/work-areas/useResizableBoxHeight';
import { WorkAreaBoxResizeHandle } from './WorkAreaBoxResizeHandle';
import {
  documentTitleForPath,
  openedItemKeyOf,
  phoneHeaderScope,
  tabNameOfSelected,
} from '@/utils/documentTitle';
import { usePeekedWorkItemStore } from '@/stores/workItemStoreSlot';
import {
  addressFrameAt,
  frameWithNamedNamespacePage,
  isAddressErrorFrame,
} from '@/routing/workAreaRouteState';
import { WorkAreaScopeGate } from './WorkAreaScopeGate';
import { useDecisionsWaitingRead } from './useDecisionsWaitingRead';
import { lazyRoute } from '@/routing/lazyRoute';
import { CommandPaletteSlot, preloadCommandPalette } from '@/features/command/CommandPaletteSlot';
import { NewDashboardPageDialog } from '@/features/dashboard/NewDashboardPageDialogChunk';
import { ModalFrame } from '@/components/ModalFrame';
import { PageNavigationList } from '@/features/dashboard/PageNavigationList';
import { HomeNavigationGroup } from '@/features/dashboard/HomeNavigationGroup';
import { guestLanding, guestLandingPhoneRows } from '@/features/dashboard/guestLanding';
import {
  AllWorkAreasViewsList,
  preloadSidebarViews,
  ViewNavigationList,
} from '@/features/views/ViewNavigationListChunk';
import { viewNameOfUrlId } from '@/features/views/viewNames';
import { useReaderViewName } from '@/features/views/packageViewName';
import { gateControlProps } from '@/utils/gateControl';
import { guestFrameOffers, workAreaGate } from '@/utils/workAreaPermissionGate';
import { openFeedbackSheet } from '@/features/feedback/capture/FeedbackSheetHost';
import {
  everyGate,
  namespaceCreateGate,
  type WorkAreaGateType,
} from '@/utils/workAreaPermissionGate';
import {
  dashboardPageEditNavigationState,
  dashboardPageIdFromPath,
  projectDashboardPageEditPath,
  projectDashboardPagePath,
} from '@/features/dashboard/dashboardUrls';
import { useRefetchOnServiceRecovery } from '@/hooks/useRefetchOnServiceRecovery';
import { useUnsavedChangesActions } from '@/features/settings/UnsavedChangesProvider';
import {
  DesktopCommandFailureNoticeChunk,
  DesktopTabBarChunk,
  DesktopUpdateButtonChunk,
} from '@/desktop/desktopChrome';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { DesktopTitleBarPlaceholder } from '@/desktop/DesktopTitleBarSlot';
import { isShellRuntime, shellDrawsNavigation } from '@/shell/shellDetection';
import { useShellScreenTitle } from '@/shell/shellScreenTitle';
import { announcedRecordTitle } from '@/shell/recordTitle';
import { useShellKeyCommands } from '@/shell/shellKeyCommands';
import { drawsShellHistoryNav, ShellHistoryNav } from '@/shell/ShellHistoryNav';
import { postShellMessage } from '@/shell/shellBridge';
import { ROW_OVERLAYS_MAIN, usePhoneRowRoom } from '@/shell/phoneRowRoom';
import type { ILayoutOutletContext } from '@/shell/MoreScreen';
import {
  addressFollowsAppScope,
  appScopeLineShownAt,
  appScopeSwitcherActive,
  appScopeTitleIn,
  appScopeTitleOf,
} from '@/shell/appScope';
import {
  AppScopeLine,
  AppScopeNotice,
  openWorkAreaSwitcher,
  orderedWorkAreas,
} from '@/shell/AppScopeLine';
import { ConfirmationToast } from '@/components/ConfirmationToast';
import {
  APP_SCOPE_NOTICE_MS,
  appScopeFollowingFrom,
  chooseAppScopeEverywhere,
  useAppScope,
} from '@/shell/useAppScope';
import {
  CreateWorkAreaActionContext,
  type ICreateWorkAreaAction,
} from '@/features/work-areas/createWorkAreaAction';
import {
  createOrganisationGate,
  createWorkAreaRefusal,
} from '@/features/work-areas/createWorkAreaGate';
import {
  drawerNewWorkAreaPath,
  takeNewWorkAreaIntent,
} from '@/features/work-areas/newWorkAreaIntent';
import {
  isAllWorkAreasScope,
  isNamespaceScopedPath,
  isWorkAreaOverviewPath,
  namespaceActivityPath,
  namespaceIdOfPath,
  pageOwnsItemSelection,
  sidebarHomePath,
  WORK_AREAS_HOME_PATH,
} from '@/features/work-areas/workAreaOverviewUrls';
import {
  decisionsWaitingInScope,
  decisionsWaitingUnknownInScope,
} from '@/features/decisions/decisionsWaitingInScope';
import {
  tasksUrgentInScope,
  tasksWaitingInScope,
  tasksWaitingUnknownInScope,
} from '@/features/tasks/tasksWaitingInScope';
import { useWorkAreaHasParts } from '@/features/parts/useWorkAreaHasParts';
import {
  sidebarAccountItems,
  sidebarAccountItemsContextFor,
  schedulesPathFor,
  sidebarNavItems,
} from './sidebarNavItems';
import { pageName } from '@/shell/shellWords';
import { phoneRowClasses } from './phoneRowClasses';
import { phonePageKindOf } from '@/shell/routeParents';
import { usePhoneNavigation } from '@/shell/usePhoneNavigation';
import { useSinglePageScroller } from '@/shell/useSinglePageScroller';
import { loadedAsIfBanner } from '@/features/as-if/loadAsIfUi';
import { AsIfNotHereGate } from '@/features/as-if/AsIfNotHere';
import { useKeepPosition } from '@/shell/useKeepPosition';
import { scopeHeldAt } from '@/features/work-items/fullPageOrigin';
import {
  addressErrorRowsOf,
  addressErrorWayBackOf,
  usePhoneSubPageOpen,
  usePhoneChromeRows,
} from '@/shell/phoneChromeCore';
import { usePhoneChromeModule } from '@/shell/loadPhoneChrome';
import { useLinkedRootRows } from '@/shell/linkedRoot';
import { ShellDeploymentSwitch } from '@/shell/ShellDeploymentSwitch';
import { useWorkAreaNavigationPreferences } from '@/features/work-areas/useWorkAreaNavigationPreferences';
import {
  isUnscopedSingleWorkAreaRoute,
  allScopeIsPending,
  allScopeTargetFor,
  appHomeTargetFor,
  pathForAllWorkAreas,
  pathForWorkAreaSwitch,
} from '@/features/work-areas/workAreaScopeRoutes';
import { useTouchReveal } from '@/features/work-areas/workAreaDragHandle';
import {
  NAMESPACE_ACTIVITY_SEGMENT,
  NAMESPACE_DECISIONS_SEGMENT,
  NAMESPACE_TASKS_SEGMENT,
  NAMESPACE_VIEWS_SEGMENT,
} from '@/features/work-areas/workAreaOverviewUrls';
import {
  workAreaSelectorDisabled,
  guestScopeName,
  workAreaSelectorLabel,
  scopeNameIsPending,
  scopeLineHeldForMark,
  SCOPE_MARK_HOLD_MAX_MS,
} from '@/features/work-areas/workAreaSelector';
import { useViewedPublicWorkArea } from '@/features/public-read/viewedPublicWorkArea';
import { findWorkAreaByRouteRef, workAreaRouteRefOf } from '@/routing/workAreaRoute';
import { useDashboardPageTitle } from './useDashboardPageTitle';
import { isWorkAreaBoundaryHandle } from '@/routing/addressRoutes';
import { pageWorkAreaIdFor } from '@/routing/pageWorkAreaId';
import { PageWorkAreaContext } from '@/routing/pageWorkAreaContext';
import { openSettingsWorkArea } from '@/features/settings/settingsWorkAreaChoice';
import { ORGANISATION_FLOW_PATH } from '@/features/organisations/organisationFlowPath';
import { SHARED_WITH_YOU_PATH, useSharedWithYouCount } from '@/features/sharing/sharedWithYou';
import { isGuestOnlyReader, landsOnSharedWithYou } from '@/features/sharing/guestOnlyLanding';
import { OrganisationLogoMark } from '@/features/organisations/OrganisationLogoMarkChunk';
import { useMyIdeasCount } from '@/features/ideas/myIdeas';
import { drawerPanelPicks } from '@/shell/drawerPanelScope';
import { Waiting, WaitingSlowLine } from './Waiting';
import { RoutePendingFrame } from '@/routing/RoutePendingFrame';
import { useLocale, useT } from '@/i18n/LocaleProvider';
import { useFormat } from '@/i18n/useFormat';

// Its `allWorkAreas` words load with it, not with the launch entry (F-0621); the Suspense around the
// routes holds the page's own wait (`pagePending`) for the tick it takes.
const NoWorkAreaEmptyState = lazyRoute(
  () => import('@/features/work-areas/NoWorkAreaEmptyState'),
  (module) => module.NoWorkAreaEmptyState,
);

/**
 * The phone's work-area sheet (F-0416), drawn only once its row is tapped: its own chunk, not the
 * launch tab's entry (T-0559 entry budget, gate 478). Fetched once the launch is idle, so the first
 * tap opens it at once.
 */
/**
 * The Create work area dialog, mounted once it is first opened and kept mounted after, so a draft
 * survives closing it as it always did: its own chunk, fetched once the launch is idle (gate 478).
 */
const CreateProjectDialog = lazyRoute(
  () => import('./CreateProjectDialog'),
  (module) => module.CreateProjectDialog,
);

/**
 * The Sign out dialog (I-2092): drawn only once a Sign out is pressed, so its own chunk, not the
 * launch tab's entry (T-0559 entry budget, gate 478).
 */
const SignOutDialog = lazyRoute(
  () => import('@/features/auth/SignOutDialog'),
  (module) => module.SignOutDialog,
);

const WorkAreaPickerSheet = lazyRoute(
  () => import('./WorkAreaPickerSheet'),
  (module) => module.WorkAreaPickerSheet,
);

/** Runs `fetch` once the launch is done and the browser is idle; returns its cancel. */
function whenIdle(fetch: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(fetch, { timeout: 3_000 });
    return () => window.cancelIdleCallback(handle);
  }
  const timer = setTimeout(fetch, 1_500);
  return () => clearTimeout(timer);
}

/** I-0689: an item opened under All work areas, over the page. Loaded only when one is named. */
// I-1128: through `lazyRoute`, so a dropped chunk is retried and a page being left reports nothing.
const AllWorkAreasItemPanel = lazyRoute(
  () => import('@/features/work-areas/AllWorkAreasItemPanel'),
  (module) => module.AllWorkAreasItemPanel,
);

/**
 * PX-14: the passkey offer after a one-tap entry, with its `invitations` words: its own chunk, drawn
 * only on a launch that has the offer pending, not the launch tab's entry (F-0621).
 */
const PasskeyOffer = lazyRoute(
  () => import('@/features/access/PasskeyOffer'),
  (module) => module.PasskeyOffer,
);

const PROJECT_SELECTOR_EXPANDED_SESSION_KEY = 'featit:projectSelectorExpanded';

function navLinkClasses({ isActive }: { isActive: boolean }): string {
  return `flex h-7 items-center gap-2 rounded-md px-2 text-[13px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
    isActive
      ? 'bg-[#D7DBDC]/75 text-gray-950 dark:bg-[#303030] dark:text-white'
      : 'text-gray-500 hover:bg-[#D7DBDC]/50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-[#242424] dark:hover:text-gray-100'
  }`;
}

function initialProjectSelectorExpanded(): boolean {
  if (typeof window === 'undefined') return false;
  return deviceSession.getItem(PROJECT_SELECTOR_EXPANDED_SESSION_KEY) === 'true';
}

export const Layout = observer(function Layout() {
  const t = useT();
  const format = useFormat();
  // F-0709: the as-if banner and its words are their own chunk. The boot of a tab that holds a view awaits
  // it before the first render (`index.tsx`), so the banner is in its first commit; any other tab has none.
  const LoadedAsIfBanner = loadedAsIfBanner();
  const navigate = useNavigate();
  const location = useLocation();
  const committedRouteLocation = useCommittedRouteLocation();
  const matches = useMatches();
  const { runAfterDiscard } = useUnsavedChangesActions();
  const {
    projectStore,
    authStore,
    dashboardPageStore,
    decisionsStore,
    tasksStore,
    namespaceStore,
    viewHierarchyStore,
    notificationStore,
  } = useStores();
  /*
   * T-0559: the namespace tree, which every namespace-scoped page reads (its work areas, its banner's
   * name, the scope line), is loaded by the shell itself, at every width. It was loaded only by the
   * pinned sidebar's work-area picker, which is not drawn below `lg` (cut B): Activity under a
   * namespace at 768 said "No work areas are available." under a banner naming the namespace's id.
   */
  useEffect(() => {
    if (namespaceStore && namespaceStore.tree === null && !namespaceStore.loading) {
      void namespaceStore.load();
    }
  }, [namespaceStore]);
  /*
   * T-0968: the selector's open list and the update slots' filled parts are chunks of their own,
   * read once the launch's own requests are done: before anyone opens the list, and before a new
   * build can replace the chunks this page was built with.
   */
  useEffect(() => {
    void afterBootRequests().then(
      () => {
        WorkAreaPickerOptions.preload();
        preloadDeploymentUpdateParts();
      },
      () => undefined,
    );
  }, []);
  // Read once, so the offer stays mounted through its own "Passkey added" after it forgets the flag.
  const [passkeyOffered] = useState(passkeyOfferPending);
  // F-0743 §4: the search scopes of the views on screen, which the one bar searches.
  const [viewSearchScopes] = useState(() => new ViewSearchScopes());
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  // Mounted from its first opening on, so closing keeps its draft (`CreateProjectDialog` above).
  const [createProjectWanted, setCreateProjectWanted] = useState(false);
  useEffect(() => {
    if (createProjectOpen) setCreateProjectWanted(true);
  }, [createProjectOpen]);
  // The namespace a "New work area here" asked for (I-0655, AC-M11); null means the person's own.
  const [createProjectNamespaceId, setCreateProjectNamespaceId] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  /*
    The page mounts once this layout's own effects have run: the work-area list read, the decisions
    scope, the selection from the address. A page whose code was fetched at boot (T-0559) used to
    draw in this layout's first commit, so its effects ran first and this layout's then overrode
    them: the list read twice, the Activity feed torn out and asked again, a namespace's decisions
    replaced by the remembered work area's (gate 474a). Set in a layout effect, the second render
    comes before the first paint, and React runs the first commit's effects before it: nothing is
    drawn in between, and no Suspense fallback is revealed.
  */
  const [layoutEffectsRan, setLayoutEffectsRan] = useState(false);
  useLayoutEffect(() => setLayoutEffectsRan(true), []);
  // In Layout, not on a page: Cmd+K has to answer from wherever you are, and the palette's own
  // navigation is the thing that gets you off a page you cannot navigate from.
  // F-0743 §4 (VSB-34): where the one bar is at the top, ⌘K is the bar and no palette opens.
  const { isCommandPaletteOpen, setCommandPaletteOpen } = useKeyboardShortcuts({
    openBar: openSearchBar,
  });
  // Once opened, it stays mounted, so closing it animates and reopening is instant.
  const [commandPaletteWanted, setCommandPaletteWanted] = useState(false);
  useEffect(() => {
    if (isCommandPaletteOpen) setCommandPaletteWanted(true);
  }, [isCommandPaletteOpen]);
  // The sidebar's view tree is drawn as soon as the work area is known: its chunk is asked for now,
  // beside the work area's reads, not once the group mounts (T-0591).
  useEffect(preloadSidebarViews, []);
  // Fetched once the launch is done and the browser is idle, so the first ⌘K opens at once.
  useEffect(
    () =>
      whenIdle(() => {
        void preloadCommandPalette();
        void CreateProjectDialog.preload();
        // I-1895: once per start, after the first screen, every local-sync store loses its retired
        // records, also a work area's this device never opens again. Loaded here, off the launch entry.
        void import('@/local-sync/purgeRetiredStores')
          .then((module) => module.purgeRetiredStoresOnce())
          .catch(() => undefined);
      }),
    [],
  );
  // F-0235: the same commands, listed in the iPad's ⌘-hold overlay when the app can show them.
  useShellKeyCommands({
    palette: () => {
      if (!openSearchBar()) setCommandPaletteOpen(true);
    },
    'new-item': () =>
      runAfterDiscard(() =>
        navigate(projectNewItemPath(workAreaRouteRefOf(projectStore.selectedProject))),
      ),
    search: focusGlobalSearch,
  });

  useEffect(() => {
    projectStore.fetchProjects();
  }, [projectStore]);

  // F-0530 (AC-19.1, AC-19.6): the app's one scope, in the iPhone app with `workAreaSwitcher`.
  const scopeSwitcher = appScopeSwitcherActive();
  const appScopeNotice = useAppScope();

  // ⭐ An overview URL IS the All scope — a link or reload straight into `/work-areas/*` records it, so
  // the next view the reader opens keeps it (I-0405).
  // RD-24: not while the address is a tab root on its way to the app's scope — it is the scope left.
  const addressFollows = addressFollowsAppScope(
    location.pathname,
    projectStore.appScope,
    projectStore.projects,
    appScopeFollowingFrom(),
  );
  useEffect(() => {
    if (addressFollows) return;
    if (isWorkAreaOverviewPath(location.pathname) && !projectStore.allWorkAreas) {
      // This window's scope only (I-1118): an address is not the person choosing All.
      projectStore.selectAllWorkAreas({ remember: false });
    }
  }, [addressFollows, location.pathname, projectStore, projectStore.allWorkAreas]);

  /*
    F-0383, §10.9 NV-6: a namespace-scoped URL IS the namespace scope, the same reason an
    All-work-areas URL is that scope above — a cold link or reload straight into
    `/-/namespaces/:namespaceId/*` hydrates ProjectStore from the address rather than waiting for a
    click that already set it, so a fresh tab opens in the namespace the link named.
  */
  useEffect(() => {
    const namespaceId = isNamespaceScopedPath(location.pathname)
      ? namespaceIdOfPath(location.pathname)
      : null;
    if (namespaceId && projectStore.selectedNamespaceId !== namespaceId) {
      projectStore.selectNamespace(namespaceId);
    }
  }, [location.pathname, projectStore, projectStore.selectedNamespaceId]);

  /** Choosing All work areas keeps the reader on a view that supports it (I-0405). */
  function chooseAllWorkAreas(): void {
    // F-0530 (AC-19.1): in the app, the person's choice is the app's scope, on every tab.
    if (scopeSwitcher) chooseAppScopeEverywhere(projectStore, SHELL_ALL_WORK_AREAS_SCOPE);
    else projectStore.selectAllWorkAreas();
    const next = pathForAllWorkAreas(location.pathname);
    if (next && next !== location.pathname) navigate(next);
  }

  /**
   * Choosing a namespace (F-0383, §10.9): the same scoping `chooseAllWorkAreas` does, landing on
   * the namespace's Activity — the same first view All work areas lands on when nothing else
   * applies. Staying on an equivalent view when one already supports the scope (the way
   * `pathForAllWorkAreas` does) is the routing work that follows this click wiring, not part of it.
   */
  function chooseNamespace(namespaceId: string): void {
    projectStore.selectNamespace(namespaceId);
    navigate(namespaceActivityPath(namespaceId));
  }
  // A deploy restarts the backend under every open tab, so the fetch above fails for a reason that
  // resolves itself seconds later. Without this the switcher held that failure until somebody
  // noticed a small red banner and pressed retry, or reloaded the page.
  useRefetchOnServiceRecovery(useCallback(() => void projectStore.fetchProjects(), [projectStore]));

  // Both halves of the title come from the route. Reading the page out of the *selected* work area
  // instead put the wrong work area's pages in front of the lookup on any render where selection
  // and route disagree — the same render `useWorkAreaRouteProject` exists to survive (I-0204).
  const here = parseAddress(location.pathname);
  const dashboardRoute = here.kind === 'work-area' ? /^\/pages\/([^/]+)/.exec(here.route) : null;
  const routeWorkArea =
    here.kind === 'work-area'
      ? findWorkAreaByRouteRef(projectStore.projects, here.reference)
      : null;
  const dashboardPageTitle = useDashboardPageTitle(
    here.kind === 'work-area' && dashboardRoute
      ? dashboardPageStore
          .pages(routeWorkArea?.id ?? projectStore.selectedProjectId)
          .find((page) => page.id === decodeURIComponent(dashboardRoute[1]))?.title
      : undefined,
    routeWorkArea?.id ?? projectStore.selectedProjectId,
  );
  // AGM-51 (7): a guest's landing page — the first page shared with them, where Home leads — is a
  // root on the phone. A page opened for one item (`pages/:pageId/items/:key`) is not their landing.
  const guestLandingHere =
    here.kind === 'work-area' &&
    dashboardRoute &&
    /^\/pages\/[^/]+\/?$/.test(here.route) &&
    routeWorkArea &&
    isGuestWorkArea(routeWorkArea)
      ? guestLanding({ pages: dashboardPageStore.pages(routeWorkArea.id), settled: true })
      : null;
  const guestLandingRow =
    guestLandingHere?.kind === 'page' &&
    dashboardRoute &&
    guestLandingHere.pageId === decodeURIComponent(dashboardRoute[1]) &&
    dashboardPageTitle
      ? { title: dashboardPageTitle, path: location.pathname }
      : null;
  // IL-18b (I-2531): Back to a custom page of this work area names it by its title, read here so the
  // row follows the pages as they load.
  const routePages = routeWorkArea ? dashboardPageStore.pages(routeWorkArea.id) : [];
  const pageTitleOf = (pathname: string): string | null => {
    const address = parseAddress(pathname);
    const pageId = dashboardPageIdFromPath(pathname);
    // Only this work area's pages are read, so a page of another is never named by one of these.
    if (address.kind !== 'work-area' || here.kind !== 'work-area' || !pageId) return null;
    if (address.reference !== here.reference) return null;
    return routePages.find((page) => page.id === pageId)?.title ?? null;
  };
  // AGM-51 (8, I-2526): a guest's other screens go back Home, the page Home opens, never to Views.
  const guestHomeRow =
    routeWorkArea && isGuestWorkArea(routeWorkArea)
      ? {
          label: t('chromeTitles.home'),
          path: projectDashboardPagePath(
            workAreaRouteRef(routeWorkArea),
            guestLandingHere?.kind === 'page' ? guestLandingHere.pageId : null,
          ),
        }
      : null;
  // I-1366 (IL-18a): a view opened from Views is called by its own name, found as the page finds it.
  const viewRoute = here.kind === 'work-area' ? /^\/views\/([^/]+)/.exec(here.route) : null;
  const viewWorkAreaId =
    here.kind === 'work-area' && viewRoute
      ? (findWorkAreaByRouteRef(projectStore.projects, here.reference)?.id ??
        projectStore.selectedProjectId)
      : null;
  const openedViewName =
    viewRoute && viewWorkAreaId
      ? (viewNameOfUrlId(
          viewHierarchyStore.views(viewWorkAreaId),
          decodeURIComponent(viewRoute[1]),
          t,
        ) ?? undefined)
      : undefined;
  // I-1977: in the reader's language — a built-in's name, a package's view's name.
  const openedViewTitle = useReaderViewName(
    viewWorkAreaId,
    viewRoute ? decodeURIComponent(viewRoute[1]) : null,
    openedViewName,
  );
  /*
    F-0250, I-0793: an address with no page behind it is named for what the screen draws there,
    never by its shape or by the last work area visited — `/acme/tracker/-/nope` has a work area's
    home's shape and the header said "Home"; `/nosuchns` a namespace's, and the switcher kept
    naming the work area the reader came from. One frame names it for the header, the tab and the
    switcher alike.
  */
  const viewedNamespacePage = useViewedPublicWorkArea();
  const addressFrame = frameWithNamedNamespacePage(
    addressFrameAt({
      pathname: location.pathname,
      matches,
      projects: projectStore.projects,
      answered: projectStore.projects.length > 0 || projectStore.hasWorkAreaAnswer,
    }),
    location.pathname,
    viewedNamespacePage,
  );
  useDecisionsWaitingRead(addressFrame.workArea);
  const { title: routeStateTitle, scope: routeStateScope } = addressFrame;
  // I-2362: what the frame is called on screen, in the reader's language (`addressFrame.label`).
  const routeStateWords = addressFrame.label ? chromeLabelText(addressFrame.label, t) : null;
  const updateScopeLine = deploymentUpdateScopeLine(useDeploymentUpdate(), t);
  /*
    A missing page on a work area that exists is that work area's, as its pages are: arriving
    selects it (`enterWorkArea`), so the sidebar's lines lead where the page's own link does.
  */
  const frameWorkAreaId = addressFrame.workArea?.id;
  /*
    I-1024: an app address the router's catch-all answered — no declared route reads it — is never
    about the selected work area: it draws `AddressNotFound`, which names no work area of its own
    (`NoPageHere`'s `workArea` is `undefined` for it). Routing it through `pageWorkAreaId` anyway
    tied its mount to `scope.enter`: the catch-all rendered immediately (an app address resolves to
    `no-page` before the project list even answers), then the list arrived, `selectedProjectId`
    went from unset to the remembered work area, `WorkAreaScopeGate` saw its `workAreaId` go from
    falsy to a not-yet-entered one and swapped to `fallback` for one tick, and the card the catch-all
    had just drawn was torn out and put back — on `/-/nope` only, never on a declared page.
  */
  const boundaryMatched = matches.some((match) => isWorkAreaBoundaryHandle(match.handle));
  const pageWorkAreaId = pageWorkAreaIdFor({
    here,
    allWorkAreas: projectStore.allWorkAreas,
    boundaryMatched,
    selectedProjectId: projectStore.selectedProjectId,
    projects: projectStore.projects,
  });
  /*
    I-0958 WS-5: the work area the reader has just chosen in the switcher, until the page on screen
    is about it. Cleared by the page reaching it, or by any change of address.
  */
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  useEffect(() => {
    if (switchingTo && pageWorkAreaId === switchingTo) setSwitchingTo(null);
  }, [switchingTo, pageWorkAreaId]);
  useEffect(() => {
    setSwitchingTo(null);
  }, [location.pathname]);
  const pagePending = <RoutePendingFrame />;
  // I-1205: a page opened from a panel keeps the scope the panel was in (`FullPageLink`).
  const scopeHeld = scopeHeldAt(location);
  useEffect(() => {
    if (frameWorkAreaId && !scopeHeld) projectStore.enterWorkArea(frameWorkAreaId);
  }, [frameWorkAreaId, projectStore, scopeHeld]);
  // F-0584 MWI-28 (I-2170): the first sign-in after a sign-up from a link says what it joined and
  // opens the first place; until its answer is in, neither choice below is made.
  useEffect(() => {
    void signUpInvitationLanding.land({
      t,
      enter: (workAreaId) => projectStore.enterWorkArea(workAreaId),
      refreshWorkAreas: () => void projectStore.fetchProjects({ background: true }),
    });
  }, [projectStore, t]);
  const signUpLandingPending = signUpInvitationLanding.pending;
  const signUpLandingTarget = signUpInvitationLanding.target;
  useEffect(() => {
    if (signUpLandingTarget && location.pathname === signUpLandingTarget) {
      signUpInvitationLanding.arrived();
    }
  }, [location.pathname, signUpLandingTarget]);
  // I-1118: never decided before the work-area list answers.
  const allScopeTarget = signUpLandingPending
    ? 'pending'
    : allScopeTargetFor(
        location.pathname,
        projectStore.projects,
        projectStore.hasWorkAreaAnswer,
        Boolean(projectStore.failure) && !projectStore.loading,
        projectStore.lastOpenedWorkAreaId,
      );
  // I-2216 (AC-19.21.1): the app's home names no work area, so it opens All work areas.
  const appHomeTarget = signUpLandingPending
    ? 'pending'
    : appHomeTargetFor({
        pathname: location.pathname,
        answered: projectStore.hasWorkAreaAnswer,
        failed: Boolean(projectStore.failure) && !projectStore.loading,
        memberWorkAreaCount: memberWorkAreas(projectStore.projects).length,
        inAppShell: isShellRuntime(),
      });
  /*
    ⛔ `selectedWorkAreaName`, not `selectedProject?.name`. The second resolves the remembered id
    against `projectMap`, which is empty until the work areas have been fetched — so on a route with
    no slug in it the tab had nothing to name the work area with and fell back to the product's own
    name, on every load and for the whole session whenever that fetch failed. Jay, of two tabs both
    reading "FEATit - Decisions" beside a sidebar reading Evorix: *"the browser tab does not show the
    correct project"* (I-0611). ⚠️ I-0065's verification measured work-area addresses only, where the address
    in the address answers the question and the store is never consulted.
  */
  const allWorkAreasScope = isAllWorkAreasScope(location.pathname, projectStore.allWorkAreas);
  /*
    F-0347, I-1023 (one rule set, per the brief): an organisation's Settings and its legacy People
    address are about ONE organisation, the same way a work area's Settings is about one work area
    — the tab and the narrow header name it, not the product. Read from `namespaceStore.tree`, the
    same list `OrganisationSettingsPage`'s own General section reads its name from; `undefined`
    until the tree answers (that page's own effect loads it), which falls through to the product's
    name below exactly as an unanswered work-area list already does (I-0611).
  */
  const organisationRouteMatch = /^\/organisations\/([^/]+)(?:\/settings)?\/?$/.exec(
    appRouteOf(location.pathname) ?? '',
  );
  const organisationName = organisationRouteMatch
    ? (namespaceStore.tree ?? []).find((namespace) => namespace.id === organisationRouteMatch[1])
        ?.name
    : undefined;
  /*
    F-0383, I-0611: a namespace's own scope (`/-/namespaces/:namespaceId`, and its `/activity`,
    `/decisions`, `/tasks`, `/views` — never the ones carrying a work area slug too, which already
    name a work area and take that branch instead) names the namespace, the same way the
    organisation match above does, and the same reason: with no namespace in the title the tab
    silently kept whichever work area happened to be selected, which is the wrong scope entirely
    for a page scoped to a different namespace (I-0611's own failure, one surface over).
  */
  const namespaceScopeRouteMatch = new RegExp(
    `^/namespaces/([^/]+)(?:/(?:${NAMESPACE_ACTIVITY_SEGMENT}|${NAMESPACE_DECISIONS_SEGMENT}|${NAMESPACE_TASKS_SEGMENT}|${NAMESPACE_VIEWS_SEGMENT}(?:/[^/]+)?))?/?$`,
  ).exec(appRouteOf(location.pathname) ?? '');
  const namespaceScopeName = namespaceScopeRouteMatch
    ? (namespaceStore.tree ?? []).find((namespace) => namespace.id === namespaceScopeRouteMatch[1])
        ?.name
    : undefined;
  const locale = useLocale();
  const titleScope = {
    allWorkAreas: allWorkAreasScope,
    workAreaId: projectStore.selectedProject?.id ?? null,
    namespaceId: projectStore.selectedNamespaceId,
  };
  const titleNamespaceOf = (workAreaId: string) =>
    projectStore.projectMap?.[workAreaId]?.namespaceId;
  const openSettingsArea = openSettingsWorkArea(
    projectStore.projects,
    projectStore.selectedProjectId,
    new URLSearchParams(location.search),
  );
  const settingsWorkAreaTitle = openSettingsArea
    ? workAreaQualifiedLabel(openSettingsArea)
    : undefined;
  // U-0060 (TT-1): the item open at `items/<key>`, by its key and title once the store holds it.
  const peekedWorkItemStore = usePeekedWorkItemStore();
  const openItemKey = openedItemKeyOf(location.pathname);
  const openItem = openItemKey
    ? peekedWorkItemStore?.getItemByKey(openItemKey, projectStore.selectedProjectId)
    : undefined;
  const titleUrgentTasks = tasksUrgentInScope(tasksStore, titleScope);
  const currentDocumentTitle = documentTitleForPath(
    t,
    location.pathname,
    projectStore.projects,
    // PX-33: a customer's tab names the agency, never the work area's own name.
    tabNameOfSelected(projectStore.selectedProject, projectStore.selectedWorkAreaName),
    routeStateWords ?? dashboardPageTitle,
    {
      allWorkAreas: allWorkAreasScope,
      scopeName: organisationName ?? namespaceScopeName ?? routeStateScope ?? undefined,
      // T-0848: the view open at `views/<id>`, by the name the bar shows.
      openedViewName: openedViewTitle,
      viewName: (english) => pageName(t, english),
      allWorkAreasName: t('navigation.picker.title'),
      // F-0774 ONE-8: Settings with a work area's section open reads as its heading does.
      settingsWorkArea: settingsWorkAreaTitle,
      // U-0060: the document open, by the title its page announces.
      documentName: announcedRecordTitle(location.pathname) ?? undefined,
      itemName: openItem ? `${openItem.key} ${openItem.title}` : undefined,
      locale,
      // AC-5: the badge's number, from the badge's rule — not a second count that can disagree.
      // I-1178: and in a namespace's scope, that namespace's number — the badge's own scope there.
      // F-0744 (AC-NB5): with the notices each list draws, as its badge counts them.
      waitingDecisions: withNotices(
        decisionsWaitingInScope(decisionsStore, titleScope),
        noticesWaitingInScope(notificationStore, 'decision', titleScope, titleNamespaceOf),
      ),
      // I-0900: same rule, for "Tasks for me".
      waitingTasks: withNotices(
        tasksWaitingInScope(tasksStore, titleScope),
        noticesWaitingInScope(notificationStore, 'task', titleScope, titleNamespaceOf),
      ),
      // I-2602 (TF-38): the count's urgent part, by the badge's rule, in the reader's words.
      urgentTasksLabel: titleUrgentTasks
        ? t('navigation.sidebar.tasksUrgent', { count: format.number(titleUrgentTasks) })
        : undefined,
    },
  );

  useLayoutEffect(() => {
    document.title = currentDocumentTitle;
  }, [currentDocumentTitle]);

  function closeMobileNav(): void {
    setMobileNavOpen(false);
  }

  /*
    The page's own name, where only the page knows it: a dashboard page, a view opened from Views,
    and a request a guest opened on its own (F-0710 CRQ-12, I-2492), which announces its title.
  */
  const barPageTitle =
    dashboardPageTitle ?? openedViewTitle ?? announcedRecordTitle(location.pathname);
  // T-0838 (IN-27): in the reader's language; the native header's `screen` title is this one too.
  const mobileViewTitle = useViewTitle(location.pathname, {
    pageTitle: barPageTitle,
    frameTitle: addressFrame.label,
  });
  /*
   * T-0320: inside the iPhone app, the native tab bar and navigation bar replace the web's own — the
   * drawer, the narrow header, the bottom bar and the desktop tab strip. Decided by the one
   * predicate and nowhere else, so a browser is unchanged.
   *
   * F-0519: not inside the iPad app. It draws no bar (`webNavigation`), so the web keeps all of its
   * navigation and lays it out by width, as the desktop app and a browser do.
   */
  const inShell = shellDrawsNavigation();
  /*
   * T-0559: the pinned sidebar is drawn only where it shows, `lg` and wider, as its `aside`'s
   * `lg:flex` says. Below that it was drawn hidden on every launch: its rows, its badges' four reads
   * (unread notifications, shared items, ideas, tasks waiting) and its view tree, for nothing on
   * screen. A phone's sidebar is the menu it opens (`SlideOverNavigation`, the More drawer's panel).
   */
  const sidebarPinned = useMediaQuery(LG_MEDIA_QUERY);
  /*
   * F-0413 (AC-18): on a phone — the iPhone app, or a browser narrower than 768 px — the More menu
   * is laid out for a finger: labelled rows, and the work area picker as one row that opens a
   * sheet. Only the two places a phone shows the navigation get it (the drawer and the More
   * screen); the pinned sidebar below is the desktop's at every width.
   */
  const navigationLayout: SidebarLayoutType = usePhoneNavigation() ? 'phone' : 'sidebar';
  // I-1304 (IL-17): the iPad app's Back and Forward, in its header — see `ShellHistoryNav`.
  const historyNav = drawsShellHistoryNav(navigationLayout === 'phone');
  /*
   * IU-1 (S-1): in the app's own shell, in phone mode, `<main>` gives up its own scrolling so the
   * document is the page's one scroller — see `useSinglePageScroller`'s own doc for why this is
   * narrower than `navigationLayout === 'phone'` alone.
   */
  const singleScroller = useSinglePageScroller();
  // IU-14: the list keeps its place across a pop, a reload and a relaunch — see the hook's own doc.
  useKeepPosition();
  /*
   * IU-3 (S-4, M-10): in phone mode the web draws its own chrome — a tab root's top row, a pushed
   * screen's back row — in place of the narrow header, the same in the app and in mobile Safari.
   * The rows read phone mode and the route table (`routeParents.ts`) and nothing else.
   */
  // I-1304 (IL-16): a root reached by a link draws the back row to the linking page.
  // I-1371: a pushed custom page's row is titled by its own name, as a pushed view's is (IL-18a).
  const routedPhoneRows = useLinkedRootRows(
    guestLandingPhoneRows(
      usePhoneChromeRows(location.pathname, barPageTitle),
      guestLandingRow,
      guestHomeRow,
    ),
  );
  /*
    F-0530 (the captain, 2026-10-06): a screen that sends you elsewhere decides before it paints.
    Under All, an unscoped Items or Roadmap is only a way to the work area it opens or to the
    choice (I-1118), and `/-/home` only a way to the all-work-areas Home: it draws none of its own
    chrome (no work-area line, no search), and the redirect runs before the first paint
    (`RedirectBeforePaint`). Measured on the device: "All work areas ▾" and search for one frame,
    then a slide to "Choose a work area".
  */
  /*
    I-1992: the app's home still waiting for the list is not a redirect yet. Such a wait can end
    where it is (one work area), and hiding the search row until then moved the page down by its
    height when the list answered (gate 849, late-chrome-no-shift).
  */
  const redirectsFromHere =
    (projectStore.allWorkAreas &&
      (isUnscopedSingleWorkAreaRoute(location.pathname) ||
        appRouteOf(location.pathname) === '/home')) ||
    (appHomeTarget !== null && appHomeTarget !== 'pending');
  /*
    A state that is not a page keeps the narrow header that names it — except an address that leads
    nowhere, which draws the back row (I-1265, AC-18.13): in the app its native bar is hidden, so
    the narrow header's absence left "Go to work items" as the only way off the screen.
  */
  const addressError = routeStateTitle !== null && isAddressErrorFrame(addressFrame);
  const addressErrorRows = addressErrorRowsOf(
    t,
    addressFrame,
    addressErrorWayBackOf(
      location.pathname,
      addressFrame.workArea
        ? workAreaRoutePath(workAreaRouteRef(addressFrame.workArea), '/items')
        : appPath('/items'),
    ),
    navigationLayout === 'phone',
  );
  const phoneRows = redirectsFromHere
    ? { topRow: null, tabRoot: null, backRow: null, pageRoot: null }
    : routeStateTitle === null
      ? routedPhoneRows
      : addressError
        ? addressErrorRows
        : { topRow: null, tabRoot: null, backRow: null, pageRoot: null };
  const phoneRow =
    phoneRows.topRow !== null || phoneRows.backRow !== null || phoneRows.pageRoot !== null;
  /*
    I-1309: the route under the New work item sheet draws its own chrome, the sheet, so it has no
    phone row; in the phone layout it has no narrow header either. It used to fall through to the
    desktop's narrow header: behind the sheet the page showed ☰, the scope, "New work item", the
    bell and the theme button, which no phone screen draws. The More panel is own-chrome too and
    keeps the header in a browser: it is where that screen's name is said (I-1065).
  */
  const ownChromeOnAPhone =
    navigationLayout === 'phone' &&
    routeStateTitle === null &&
    phonePageKindOf(location.pathname) === 'own-chrome' &&
    appRouteOf(location.pathname) !== '/more';
  const phoneChromeScreen =
    ownChromeOnAPhone ||
    phoneRows.tabRoot !== null ||
    phoneRows.backRow !== null ||
    phoneRows.pageRoot !== null;
  const phoneRowSentinel = useRef<HTMLSpanElement | null>(null);
  /*
    I-2135 (IU-3, ruled c): in a phone browser `<main>` scrolls, and the row overlays it as it
    overlays the document in the app: the stack gives its room back and `<main>` opens with it, so
    the row slides out over the page, never over a room `<main>` cannot fill (`phoneRowRoom.ts`).
  */
  const rowOverlaysMain = phoneRow && !singleScroller;
  const phoneChromeStack = useRef<HTMLDivElement | null>(null);
  usePhoneRowRoom(phoneChromeStack, rowOverlaysMain);
  /*
    F-0530 (AC-19.2): in the app a scope's root screen — a tab root, or a drawer root such as an
    all-work-areas page — has the work-area line as its first row, in the top row's place. A drawer
    root's open sub-page (a work area's Settings section) keeps its ‹ back row instead.
  */
  const subPageOpen = usePhoneSubPageOpen();
  // I-2453: the rows are drawn from their own chunk, which only phone mode loads (at launch, before
  // the first render, so they are on the first frame).
  // The bottom bar follows the width (`desktop:hidden`), so a narrow desktop app draws it too.
  const desktopWide = useMediaQuery(DESKTOP_LAYOUT_MEDIA_QUERY);
  const phoneChrome = usePhoneChromeModule(navigationLayout === 'phone' || !desktopWide);
  const MobileNavigation = phoneChrome?.MobileNavigation;
  const TopRow = phoneChrome?.TopRow;
  const BackRow = phoneChrome?.BackRow;
  const PageTopRow = phoneChrome?.PageTopRow;
  const PhoneSearchField = phoneChrome?.PhoneSearchField;
  const scopeLineRow =
    scopeSwitcher &&
    (phoneRows.topRow !== null || (phoneRows.pageRoot !== null && !subPageOpen)) &&
    appScopeLineShownAt(location.pathname);
  /*
    I-1204 (Jay's no-large-titles rule): where the app has no scope line — a plain browser, the
    iPad, an older app — a drawer root about a scope (Home, Tasks, the Parts tree, an all-work-areas
    page) draws the compact scope row a tab root draws, never a page top row with a title of its own.
    Account pages (Settings, Profile) are not about a scope and keep their titled row.
  */
  const browserScopeRow =
    !scopeSwitcher &&
    phoneRows.pageRoot !== null &&
    !subPageOpen &&
    appScopeLineShownAt(location.pathname);
  useShellScreenTitle(mobileViewTitle);
  const guestHere =
    here.kind === 'work-area' &&
    isGuestWorkArea(findWorkAreaByRouteRef(projectStore.projects, here.reference));
  /*
   * F-0743 §4 (D-0431, VSB-30): at a large form factor the one search and command bar is at the top
   * of every route, and searches the view that registered its search (VSB-32); the view then draws
   * no field of its own. ⛔ Never gated on a registered scope: a view registers only once the bar is
   * shown, so the two waited on each other and every view off F-0176's four kept its own field (the
   * S6 visual round). On a guest's work area the bar searches no work items (AGM-51,
   * `BarResultsPanel`).
   */
  const topBarVisible = routeStateTitle === null && !redirectsFromHere && !phoneRow;
  /*
   * VSB-35: on a phone the bar is the list's first thing, tucked under the top row until a pull
   * (S-5): on a tab root (Activity keeps its own toolbar's field) and a drawer root with no section
   * open. The view there registers its search with it (VSB-35d); a pushed screen keeps its own.
   */
  const phoneBarVisible =
    routeStateTitle === null &&
    !redirectsFromHere &&
    !guestHere &&
    ((phoneRows.topRow !== null && phoneRows.topRow !== 'activity') ||
      (phoneRows.pageRoot !== null && !subPageOpen));
  const viewSearchBar = useMemo(
    () => ({ scopes: viewSearchScopes, barShown: topBarVisible || phoneBarVisible }),
    [viewSearchScopes, topBarVisible, phoneBarVisible],
  );

  function handleProjectSelect(projectId: string): void {
    const project = projectStore.projectMap[projectId];
    if (!project) return;
    // I-0656, AC-M19: every destination carries the namespace in its route segment.
    const routeRef = workAreaRouteRef(project);
    runAfterDiscard(() => {
      /*
        I-0958 WS-5: a switch that moves the page is marked in the same commit as the selection, so
        main shows the page-pending state, never the previous work area's page under the new
        selection, until the navigation (a transition) puts the new work area's page on screen.
      */
      const go = (to: string) => {
        setSwitchingTo(projectId);
        navigate(to);
      };
      if (scopeSwitcher) chooseAppScopeEverywhere(projectStore, projectId);
      else projectStore.selectProject(projectId);
      // The same page in the new work area (`pathForWorkAreaSwitch`, the rule the app's other
      // switches follow too, I-1363).
      const next = pathForWorkAreaSwitch(location.pathname, location.search, routeRef);
      if (next) go(next);
    });
  }

  const sidebarProps: AppSidebarProps = {
    addressWorkArea: addressFrame.workArea,
    onNavigate: closeMobileNav,
    onCreateProject: (namespaceId) => {
      if (authStore.isAuthenticated) {
        setCreateProjectNamespaceId(namespaceId ?? null);
        setCreateProjectOpen(true);
      } else {
        navigate(appPath('/settings'));
      }
    },
    onProjectSelect: handleProjectSelect,
    onWorkAreaOverview: () => runAfterDiscard(chooseAllWorkAreas),
    onSelectNamespace: (namespaceId) => runAfterDiscard(() => chooseNamespace(namespaceId)),
    // I-2092: Sign out asks first, whether to remove the person's data from this device too.
    onSignOut: requestSignOut,
    onSignIn: () => navigate('/login'),
  };
  // I-0844: a screen with no work area to show offers the sidebar's own *New work area* dialog.
  const createWorkAreaAction: ICreateWorkAreaAction = {
    open: () => sidebarProps.onCreateProject(null),
    disabledReason: createWorkAreaRefusal(authStore),
  };
  /*
    AC-19.11 (I-1297): the dialog asked for from outside this screen — the More drawer's New work
    area, which names this screen's address with the intent on it (`newWorkAreaIntent.ts`). The dialog opens over the screen and the address is replaced by the
    plain one, so nothing is left to ask again: not a reload, and not the shell, which hears the
    plain address as this screen's. A session that may not create one gets no dialog, as its
    controls open none, and is told why once: an address is not a control that could carry the
    reason.
  */
  const [createRefusal, setCreateRefusal] = useState<string | null>(null);
  useEffect(() => {
    if (!createRefusal) return undefined;
    const timer = setTimeout(() => setCreateRefusal(null), APP_SCOPE_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [createRefusal]);
  const openCreateWorkArea = useRef(sidebarProps.onCreateProject);
  openCreateWorkArea.current = (namespaceId) => {
    if (createWorkAreaAction.disabledReason) setCreateRefusal(createWorkAreaAction.disabledReason);
    else sidebarProps.onCreateProject(namespaceId);
  };
  const addressedIntent = takeNewWorkAreaIntent(
    `${location.pathname}${location.search}${location.hash}`,
  );
  const addressedIntentPath = addressedIntent?.path ?? null;
  const addressedIntentNamespace = addressedIntent?.namespaceId ?? null;
  useEffect(() => {
    if (addressedIntentPath === null) return;
    navigate(addressedIntentPath, { replace: true });
    openCreateWorkArea.current(addressedIntentNamespace);
  }, [addressedIntentPath, addressedIntentNamespace, navigate]);
  // The More screen (`/more`) is this navigation as a page: the app's More tab, where the drawer is.
  //
  // F-0361/I-1082: `?panel=nav` is the native drawer's own embedded nav panel (`MoreMenuDrawer`,
  // `ensureMorePanelScreen`) — a second, small web view with nothing else on screen, whose job is
  // picking a destination for the *content* web view, never showing one itself. Every destination
  // this sidebar offers is still the real, unchanged `AppSidebar` (no second implementation,
  // ADR-118); only how a pick is delivered changes here — `drawerNavigate` instead of this web
  // view's own `navigate()` — and only for the two props that are not plain `<NavLink>`s already
  // (`onProjectSelect`, `onWorkAreaOverview`, `onSelectNamespace`; `DrawerNavLinkCapture` below
  // catches every `<NavLink>` the same way, generically). `onSignOut`/`onSignIn` are left as this
  // web view's own defaults — rare enough from the drawer that redirecting them too was not worth
  // the added surface for this pass; flagged in the concept doc.
  //
  // AC-19.11 (I-1297): `onCreateProject` is redirected too. The dialog opens over the content
  // screen, never in this small panel: the shell is asked to open that screen's own address with
  // the New work area intent on it, which closes the drawer.
  const isDrawerNavPanel = new URLSearchParams(location.search).get('panel') === 'nav';
  // F-0414 (AC-18.2): a view picked in the panel is still the recent view — stored first, then
  // opened by the shell. The recorder is outside the capture, so it sees the tap first.
  const panelPicks = drawerPanelPicks(projectStore, (path) =>
    postShellMessage({ type: 'drawerNavigate', path }),
  );
  // I-2453: the recorder is in the phone chunk; the panel is phone mode, so it is loaded at launch.
  const DrawerRecentViewRecorder = phoneChrome?.RecentViewRecorder;
  const drawerPanel = (
    <DrawerNavLinkCapture>
      <AppSidebar
        {...sidebarProps}
        // F-0415 (AC-18.3): on an iPhone this panel is the More menu, laid out for a finger.
        layout={navigationLayout}
        // AC-18.7: a scope picked in the panel is the panel's too (`drawerPanelPicks`).
        {...panelPicks}
        onCreateProject={(namespaceId) =>
          postShellMessage({
            type: 'drawerNavigate',
            path: drawerNewWorkAreaPath(namespaceId ?? null),
          })
        }
      />
    </DrawerNavLinkCapture>
  );
  const navigationForOutlet = isDrawerNavPanel ? (
    DrawerRecentViewRecorder ? (
      <DrawerRecentViewRecorder open={(path) => postShellMessage({ type: 'drawerNavigate', path })}>
        {drawerPanel}
      </DrawerRecentViewRecorder>
    ) : (
      drawerPanel
    )
  ) : (
    <AppSidebar {...sidebarProps} layout={navigationLayout} />
  );
  const outletContext: ILayoutOutletContext = {
    navigation: navigationForOutlet,
    // F-0530 (AC-19.4): the native switcher's Edit view is the picker's own edit mode, as a page.
    workAreaEditor: (
      <WorkAreaEditor
        onCreateProject={sidebarProps.onCreateProject}
        onProjectSelect={handleProjectSelect}
        onWorkAreaOverview={sidebarProps.onWorkAreaOverview}
        onSelectNamespace={sidebarProps.onSelectNamespace}
      />
    ),
  };

  return (
    <ViewSearchBarContext.Provider value={viewSearchBar}>
      <div
        data-layout-root=""
        // IU-3: a phone row pads itself under the status bar, so the root gives up its own top padding.
        data-phone-row={phoneRow ? '' : undefined}
        /*
         * IU-1 (S-1): `singleScroller` drops `VIEWPORT_REMAINDER` and `overflow-hidden` along with
         * `AppViewport`'s own height lock above — this box's height becomes its content's, so the
         * document is free to grow past the window and `document.scrollingElement` is the one
         * scroller. See `useSinglePageScroller`.
         */
        className={
          singleScroller
            ? // I-1309: `flex-1` here, on the row and on `<main>` hands a window taller than the page
              // down to `<main>`, so the bottom bar ends the window. It does nothing where
              // `AppViewport` sets no floor.
              'relative flex flex-1 flex-col bg-[#FEFEFE] text-gray-900 dark:bg-[#212121] dark:text-gray-100'
            : // I-1309 (IP-21): `clip`, not `hidden`. A box that hides its overflow is still a scroll
              // box a script or a focus can move: centring the last control of a long settings page
              // scrolled this root by 319 px and drew the bottom bar mid-screen over a blank band.
              // `<main>` is the one scroller here.
              `relative flex ${VIEWPORT_REMAINDER} flex-col overflow-clip bg-[#FEFEFE] text-gray-900 dark:bg-[#212121] dark:text-gray-100`
        }
      >
        {/*
        ⛔ This is no longer the element that owns the window's height — `AppViewport` is (I-0580).
        The shell takes the remainder of that column, so a bar mounted above it shrinks the shell
        instead of pushing its bottom edge off the screen, which is what happened when both this
        box and `MaintenanceNotice` each claimed a full viewport.

        `relative` is load-bearing (I-0412). An absolutely positioned element with no positioned
        ancestor is laid out against the document, not against this box, so `overflow-hidden` does
        not clip it and it makes the document itself taller. The pages' live region did exactly
        that: with a long page list it sat below the window, and the whole shell scrolled away.
      */}
        {/* IU-4: Density's stored value reaching the one attribute `phone-type.css` keys its
          Compact rule on — a setting that changes nothing must not ship. */}
        <PhoneDensityAttribute />
        {/* F-0709 ASIF-4: while a view as another role is held, on every screen, above every dialog host. */}
        {LoadedAsIfBanner ? <LoadedAsIfBanner /> : null}
        <VerificationBanner />
        {/* T-0559: its own chunk, fetched the first time it opens; until then nothing is drawn. */}
        {commandPaletteWanted ? (
          <CommandPaletteSlot open={isCommandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
        ) : null}
        {/* IU-8: mounted once, here, so it survives the navigation an answer itself causes. */}
        <DecisionAnswerToastSlot />
        {/* UXA-9: mounted once, here, so a held approval keeps its Undo after the reader leaves. */}
        <ApprovalAnswerToastSlot />
        {/* IU-9 (F-0510): mounted once, here, so it survives the pop Archive itself causes. */}
        <ItemArchiveToastSlot />
        {/* IU-10 (F-0510): mounted once, here, so it survives the navigation Add itself causes. */}
        <ItemCreatedToastSlot />
        {/* F-0584 MWI-15 (I-1935): mounted here, so it survives the navigation a Join causes. */}
        <InvitationJoinedNotice />
        {/* PX-14: once, after a one-tap entry from an invitation, so the next visit needs no link. */}
        {passkeyOffered ? (
          <Suspense fallback={null}>
            <PasskeyOffer />
          </Suspense>
        ) : null}
        {/* I-2233 (AC-PI.4): a new invitation is waiting — said once, leading to Settings. */}
        <InvitationWaitingNotice />
        {/* F-0530 (AC-19.6): "Now in X · Undo" after a link into another work area. */}
        {appScopeNotice ? <AppScopeNotice notice={appScopeNotice} /> : null}
        {createRefusal ? (
          <ConfirmationToast testId="create-work-area-refused" message={createRefusal} />
        ) : null}
        {/* T-0559: the desktop's chunk, preloaded at boot in the desktop runtime only. I-1423: until
          it has drawn, its place is held, so the page below is not moved down when it arrives. */}
        {/* I-2378: drawn as the window's first row, above every top bar (`ViewportTitleBar`). */}
        {!inShell && isDesktopRuntime() ? (
          <ViewportTitleBar>
            <Suspense fallback={<DesktopTitleBarPlaceholder />}>
              <DesktopTabBarChunk currentTitle={currentDocumentTitle} />
            </Suspense>
          </ViewportTitleBar>
        ) : null}
        <div className={singleScroller ? 'flex flex-1' : 'flex min-h-0 flex-1'}>
          {/* F-0585: its width is chosen by the handle on its right edge (`PinnedSidebar`). */}
          <PinnedSidebar pinned={sidebarPinned}>
            {sidebarPinned ? <AppSidebar {...sidebarProps} historyNav={historyNav} /> : null}
          </PinnedSidebar>

          {mobileNavOpen && !inShell && (
            <SlideOverNavigation onClose={closeMobileNav}>
              {(slideOut) => (
                <AppSidebar
                  layout={navigationLayout}
                  offersFeedback
                  addressWorkArea={addressFrame.workArea}
                  onNavigate={closeMobileNav}
                  onCreateProject={(namespaceId) => {
                    closeMobileNav();
                    if (authStore.isAuthenticated) {
                      setCreateProjectNamespaceId(namespaceId ?? null);
                      setCreateProjectOpen(true);
                    } else {
                      navigate(appPath('/settings'));
                    }
                  }}
                  onProjectSelect={handleProjectSelect}
                  onWorkAreaOverview={() =>
                    runAfterDiscard(() => {
                      closeMobileNav();
                      chooseAllWorkAreas();
                    })
                  }
                  onSelectNamespace={(namespaceId) =>
                    runAfterDiscard(() => {
                      closeMobileNav();
                      chooseNamespace(namespaceId);
                    })
                  }
                  onSignOut={() => {
                    closeMobileNav();
                    requestSignOut();
                  }}
                  onSignIn={() => {
                    closeMobileNav();
                    navigate('/login');
                  }}
                  onClose={slideOut}
                />
              )}
            </SlideOverNavigation>
          )}

          {/*
          `flex-1` stays in both: in this row it is the width the column takes beside the sidebar,
          not a height. Dropped with `min-h-0` for the single scroller, the column shrank to its
          content's width, `<main>` 283 px wide in a 390 px window, and every pane under it laid out
          that narrow (`large-text-spacing.spec.ts`, the formatting toolbar's seven controls).
        */}
          {/* F-0585 (PSW-10): on a coarse pointer the page starts 12 px further from the sidebar, so
            the sidebar handle's 44 px reach (34 px of it over the page) lies on no page control. */}
          <div
            className={
              singleScroller
                ? // IU-3: still the row's full width — the phone rows and a short page span it.
                  'flex min-w-0 flex-1 flex-col lg:[@media(pointer:coarse)]:pl-3'
                : 'flex min-h-0 min-w-0 flex-1 flex-col lg:[@media(pointer:coarse)]:pl-3'
            }
          >
            {/*
            One sticky stack above the outlet: the narrow title bar, then the search field. Both sit
            outside `<main>`, so nothing here is rebuilt when the route under it changes. On wide
            viewports the title bar is hidden and this is the top bar — there was none before.
          */}
            <div
              ref={phoneChromeStack}
              data-phone-chrome-stack={
                phoneRow ? (rowOverlaysMain ? ROW_OVERLAYS_MAIN : '') : undefined
              }
              className={
                phoneRow
                  ? // IU-3 (M-10): the row is clear at rest and takes its material itself, once stuck.
                    // I-1325: the stack keeps the row's room while the row moves out with the page, so
                    // it takes no press itself: the band the row leaves belongs to the page under it.
                    // I-2135: over `<main>` in a browser, whose spacer takes the room instead.
                    `pointer-events-none sticky top-0 z-30 [&>*]:pointer-events-auto${
                      rowOverlaysMain ? ' mb-[calc(-1*var(--phone-row-room,0px))]' : ''
                    }`
                  : 'sticky top-0 z-30 bg-[#FEFEFE]/95 backdrop-blur dark:bg-[#212121]/95'
              }
            >
              {TopRow && (phoneRows.topRow || scopeLineRow || browserScopeRow) ? (
                <TopRow
                  sentinel={phoneRowSentinel}
                  withState={!scopeSwitcher}
                  scope={
                    /*
                    F-0530 (AC-19.2, Jay: "WE DONT NEED THE VIEW TITLE"): in the app the first row is
                    the work-area line — no page title, the tab bar names the view.
                  */
                    scopeSwitcher ? (
                      scopeLineRow ? (
                        <AppScopeLine />
                      ) : null
                    ) : (
                      <TopRowScope
                        addressWorkArea={addressFrame.workArea}
                        onCreateProject={sidebarProps.onCreateProject}
                        onProjectSelect={handleProjectSelect}
                        onWorkAreaOverview={sidebarProps.onWorkAreaOverview}
                        onSelectNamespace={sidebarProps.onSelectNamespace}
                      />
                    )
                  }
                />
              ) : null}
              {BackRow && phoneRows.backRow ? (
                <BackRow
                  parent={phoneRows.backRow}
                  pageTitleOf={pageTitleOf}
                  sentinel={phoneRowSentinel}
                  addressError={addressError}
                />
              ) : null}
              {PageTopRow && phoneRows.pageRoot && !scopeLineRow && !browserScopeRow ? (
                <PageTopRow
                  title={phoneRows.pageRoot.title}
                  sentinel={phoneRowSentinel}
                  // F-0745 (OL-8): a customer's landing is a root at 390 with no scope line, so
                  // the agency's logo, named, is how they meet it there.
                  mark={
                    guestHere && routeWorkArea?.namespaceId ? (
                      <OrganisationLogoMark namespaceId={routeWorkArea.namespaceId} size="small" />
                    ) : undefined
                  }
                />
              ) : null}
              {/* IU-3: a tab root and a pushed screen draw no view name at the top (S-4). */}
              {!inShell && !phoneChromeScreen && (
                <header
                  data-touch-targets=""
                  className="mobile-top-bar flex min-h-[48px] items-center gap-2 border-b border-gray-100 px-3 dark:border-white/5 lg:hidden"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('navigation.sidebar.openNavigation')}
                    title={t('navigation.sidebar.openNavigation')}
                    className="h-touch w-touch px-0"
                    onClick={() => setMobileNavOpen(true)}
                  >
                    {/*
                    F-0235: from 768 px this is the desktop layout, and the sidebar slides over
                    until it is pinned at 1024 — so the button shows the sidebar, not a menu.
                  */}
                    <Menu className="h-5 w-5 touch-glyph desktop:hidden" />
                    <PanelLeftOpen className="hidden h-5 w-5 touch-glyph desktop:block" />
                  </Button>
                  {historyNav ? <ShellHistoryNav /> : null}
                  {/*
                    F-0745 (OL-8): below 1024 px the frame's sidebar is folded away, so a customer
                    meets the agency by its logo here, beside the scope line that names it.
                  */}
                  {guestHere && routeWorkArea?.namespaceId ? (
                    <OrganisationLogoMark
                      namespaceId={routeWorkArea.namespaceId}
                      size="small"
                      decorative
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    {/*
                    I-0781 (QA 267): while an update has something to say — a draft in the way, an
                    outgrown page, a page that reloaded — the scope line says it. Same line, same
                    height, so nothing moves; the slot beside the bell is how to act on it.
                  */}
                    {/*
                    I-1314 (CFC-f5c): a bar's line, so its type follows the text size to a cap and
                    stays there, as the top row's and a sheet's bar do: 15/20 for the scope, 20/25
                    for the title. Written in px they became `rem` in the build and the header was
                    150 px high at the largest size.
                  */}
                    <div
                      data-testid="narrow-header-scope"
                      className={`truncate text-[length:min(0.6875rem,15px)] leading-[min(1.03125rem,20px)] font-medium ${
                        updateScopeLine ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400'
                      }`}
                    >
                      {updateScopeLine ??
                        organisationName ??
                        namespaceScopeName ??
                        routeStateScope ??
                        phoneHeaderScope(
                          t,
                          location.pathname,
                          projectStore.projects,
                          projectStore.selectedProject,
                          projectStore.allWorkAreas,
                          settingsWorkAreaTitle,
                        )}
                    </div>
                    <div
                      data-testid="narrow-header-title"
                      className="truncate text-[length:min(0.875rem,20px)] leading-[min(1.3125rem,25px)] font-semibold text-gray-950 dark:text-white"
                    >
                      {mobileViewTitle}
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-1">
                    {/* Always reserved, so the title never re-flows when an update arrives. */}
                    <DeploymentUpdateHeaderSlot />
                    <ThemeToggle menuPlacement="bottom" menuAlign="right" />
                  </div>
                </header>
              )}

              {topBarVisible && (
                <div className="border-b border-gray-100 px-3 py-2 dark:border-white/5 sm:px-6 lg:px-8">
                  <GlobalSearchBar placement="top" guest={guestHere} />
                </div>
              )}
            </div>

            <main
              className={
                /*
                 * IU-9 (176 ± 4 at 402×874, the captain's ruling): a pushed phone screen (phone
                 * mode, with the back row drawn — `phoneRows.backRow`, never an `isDecision` check,
                 * since the gap this closes is `<main>`'s own, not a decision's) cancels this top
                 * padding — the density table's own "first row" for a pushed record is the back
                 * row's bottom exactly, nothing between it and the title. A tab root keeps its top
                 * padding (the row sits above the content there too, but a tab root's own target —
                 * S-4's density numbers — was never measured against zero gap the way IU-9's is), and
                 * so does desktop (`phoneRows.backRow` is `null` outside phone mode already).
                 */
                (phoneRows.backRow !== null ? 'pt-0 pb-3' : 'py-3') +
                (singleScroller
                  ? // `clip`, not `hidden`: `hidden` makes `<main>` a scroll container (its other axis computes to
                    // `auto`), and a sticky heading inside it then sticks to a box that never scrolls —
                    // the Decisions, Tasks and Activity headings did not pin in the app (I-1101).
                    ' min-w-0 flex-1 overflow-x-clip bg-[#FEFEFE] px-3 dark:bg-[#212121] sm:px-6 sm:py-5 lg:px-8'
                  : ' min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-[#FEFEFE] px-3 dark:bg-[#212121] sm:px-6 sm:py-5 lg:px-8') +
                // I-2135: an anchor or a focus brought into view stops under the row over `<main>`.
                (rowOverlaysMain ? ' [scroll-padding-top:var(--phone-row-room,0px)]' : '')
              }
              // I-1223: the screen on display, for a test that waits for the address's screen.
              data-route-location={committedRouteLocation}
            >
              {rowOverlaysMain ? (
                // I-2135: the row's room, inside `<main>`: the page rests under the row and scrolls
                // under it, so the row slides out over the page's own content.
                <div
                  aria-hidden="true"
                  data-phone-row-room=""
                  className="h-[var(--phone-row-room,0px)]"
                />
              ) : null}
              {PhoneSearchField && phoneBarVisible ? (
                // S-5, VSB-35: in phone mode search is not pinned; it is the first thing in a list.
                // Activity and a pushed screen have none.
                <PhoneSearchField
                  routeKey={location.pathname}
                  documentScrolls={singleScroller}
                  // I-1364: Items' search and its quick filters come back with the top row.
                  followsBand={phoneRows.topRow === 'items'}
                >
                  <GlobalSearchBar guest={guestHere} />
                </PhoneSearchField>
              ) : null}
              {phoneRow ? (
                /*
                IU-3 (M-10): the rows take their material once this has passed under them — the
                content's top, under the search field resting there, so the row is clear at rest.
              */
                <span
                  ref={phoneRowSentinel}
                  aria-hidden="true"
                  data-phone-row-sentinel=""
                  className="-mb-px block h-px"
                />
              ) : null}
              <CreateWorkAreaActionContext.Provider value={createWorkAreaAction}>
                <Suspense fallback={pagePending}>
                  {/*
                ⛔ Under the All scope, an unscoped route that reads its work area from the selection
                would silently open the remembered one. It is replaced by the explicit choice (I-0405).
              */}
                  {!layoutEffectsRan ? (
                    <div data-route-pending="" className="min-h-[40vh]" />
                  ) : signUpLandingTarget && location.pathname !== signUpLandingTarget ? (
                    // F-0584 MWI-28: the place a sign-up from a link joined, until the address is it.
                    <RedirectBeforePaint to={signUpLandingTarget} />
                  ) : appHomeTarget === 'pending' ? (
                    // I-0844: the app's home says it is reading the work areas, as Items does.
                    <div data-redirect-pending="">
                      <NoWorkAreaEmptyState purpose="workItems" />
                    </div>
                  ) : appHomeTarget !== null ? (
                    /*
                    I-2216: before the All scope's I-1118 choice below, which would open the work
                    area last opened: the home names no work area, and that is All work areas. Never
                    the first work area of the list either, which nobody here opened (I-1992).
                  */
                    <RedirectBeforePaint to={appHomeTarget} />
                  ) : projectStore.allWorkAreas &&
                    isUnscopedSingleWorkAreaRoute(location.pathname) ? (
                    allScopeTarget === 'pending' ? (
                      // Not this screen yet, and not a screen of its own, but never a blank page: what
                      // it waits on is the work-area list, and the wait says so (lane-loadstuck).
                      <div
                        data-redirect-pending=""
                        className="grid min-h-[40vh] place-items-center"
                      >
                        <Waiting
                          size="section"
                          label={t('navigation.selector.loading')}
                          caption={t('navigation.selector.loading')}
                          onRetry={() => void projectStore.fetchProjects()}
                        />
                      </div>
                    ) : (
                      <RedirectBeforePaint to={allScopeTarget} />
                    )
                  ) : projectStore.allWorkAreas && appRouteOf(location.pathname) === '/home' ? (
                    <RedirectBeforePaint to={WORK_AREAS_HOME_PATH} />
                  ) : /*
                    I-0958: every per-work-area store enters the page's work area before the page
                    renders, so a switch never draws the previous work area's rows under this one.
                  */
                  switchingTo && switchingTo !== pageWorkAreaId ? (
                    pagePending
                  ) : (
                    <WorkAreaScopeGate workAreaId={pageWorkAreaId} fallback={pagePending}>
                      <ExampleWorkAreaBannerSlot workAreaId={pageWorkAreaId} />
                      {/* I-2485: the page knows its work area, so a state dot, a list row and the
                          roadmap read its package's names, not only the dashboard, which scopes its
                          own (`usePackageName`). */}
                      <PageWorkAreaContext.Provider value={pageWorkAreaId}>
                        <AsIfNotHereGate>
                          <Outlet context={outletContext} />
                        </AsIfNotHereGate>
                      </PageWorkAreaContext.Provider>
                    </WorkAreaScopeGate>
                  )}
                </Suspense>
              </CreateWorkAreaActionContext.Provider>
              {phoneBarVisible ? (
                // S-5: just enough room at the end for the search field to rest under the row.
                <div
                  aria-hidden="true"
                  data-phone-search-spacer=""
                  className="h-[var(--phone-search-spacer,0px)]"
                />
              ) : null}
            </main>
            {/* I-2377: the bottom bars (the maintenance notice) sit on the tab bar, not under it. */}
            <ShellBottomBarSlot
              tabBar={
                inShell || !MobileNavigation ? null : (
                  <MobileNavigation onMore={() => setMobileNavOpen(true)} />
                )
              }
            />
          </div>
        </div>

        {/*
        I-0689: under All work areas an item opens over the page the reader is on. The Views catalog
        reads the same parameters for its own panel, so it is left to draw that one.
      */}
        {allWorkAreasScope &&
        !pageOwnsItemSelection(location.pathname) &&
        new URLSearchParams(location.search).has('item') ? (
          <Suspense fallback={null}>
            <AllWorkAreasItemPanel />
          </Suspense>
        ) : null}

        {signOutPrompt.open ? (
          <Suspense fallback={<KeyboardHoldFallback />}>
            <SignOutDialog onClose={dismissSignOut} />
          </Suspense>
        ) : null}

        {createProjectWanted ? (
          <Suspense fallback={<KeyboardHoldFallback />}>
            <CreateProjectDialog
              open={createProjectOpen}
              initialNamespaceId={createProjectNamespaceId}
              onClose={() => setCreateProjectOpen(false)}
              onCreated={(project) => {
                // I-0937: land on the new work area itself, not wherever Create was opened from — the
                // dialog opens from screens with no work area at all (I-0844), so preserving "the current
                // view" has nothing to preserve there.
                setCreateProjectOpen(false);
                /*
                AC-19.11.4 (the captain with Jay, 2026-10-09): in the app the new work area is the
                app's scope, for every tab, and the person stays where they were, as a pick in the
                native sheet does: Home is no tab root there, and opened here it was pushed into
                whichever tab this web view is (Agents showing Home).
              */
                if (scopeSwitcher) {
                  chooseAppScopeEverywhere(projectStore, project.id);
                  return;
                }
                navigate(projectHomePath(workAreaRouteRef(project)));
              }}
            />
          </Suspense>
        ) : null}
      </div>
    </ViewSearchBarContext.Provider>
  );
});

/**
 * IU-3 (S-4): the top row's scope — the work area picker behind a 44 pt button. Its own box-height
 * state is the sidebar's and unused here; the hook is called only so the one `ProjectSelector` can
 * serve both.
 */
const TopRowScope = observer(function TopRowScope(
  props: Omit<ProjectSelectorProps, 'presentation' | 'boxResize' | 'overviewMode'>,
) {
  const { authStore, projectStore } = useStores();
  const location = useLocation();
  const boxResize = useResizableBoxHeight(authStore?.user?.id ?? null);
  return (
    <ProjectSelector
      {...props}
      presentation="top-row"
      overviewMode={isAllWorkAreasScope(location.pathname, projectStore.allWorkAreas)}
      boxResize={boxResize}
    />
  );
});

/**
 * F-0530 (the captain, 2026-10-06): a redirect that runs before the browser paints — in a layout
 * effect, whose update React commits before the frame — so the screen it leaves is never seen.
 * `<Navigate>` navigates in a plain effect, after a paint of whatever was drawn around it.
 *
 * I-2056: the navigation is a transition, so while the target's route chunk is still read React
 * keeps this drawn. It says the page is still coming (`data-route-pending`, with no size, so
 * nothing moves), or the launch wait settled over an empty page and the list's skeleton drew
 * after it (LS-2).
 */
function RedirectBeforePaint({ to }: { to: string }) {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);
  return <div data-route-pending="" />;
}

/**
 * F-0530 (AC-19.4): the picker's edit mode as a page, for the native switcher's Edit view
 * (`/-/work-areas/edit`). The same `ProjectSelector`, with nothing of its own.
 */
const WorkAreaEditor = observer(function WorkAreaEditor(
  props: Omit<ProjectSelectorProps, 'presentation' | 'boxResize' | 'overviewMode'>,
) {
  const { authStore, projectStore } = useStores();
  const boxResize = useResizableBoxHeight(authStore?.user?.id ?? null);
  return (
    <ProjectSelector
      {...props}
      presentation="edit-page"
      overviewMode={projectStore.allWorkAreas}
      boxResize={boxResize}
    />
  );
});

/**
 * F-0361/I-1082: every plain `<NavLink>` the sidebar renders (every destination that is not one of
 * the three callback props `Layout` already redirects above — `onProjectSelect`,
 * `onWorkAreaOverview`, `onSelectNamespace`) — Home, a page, a view, Settings, Profile, Agents,
 * Infrastructure, Sign out — caught generically, in one place, rather than threading a
 * `drawerNavigate` prop through every row component that renders one.
 *
 * `onClickCapture` runs before the link's own `onClick`/React Router's navigation, so
 * `preventDefault` here is what stops this web view's own route from ever changing — the point of
 * the whole panel is picking a destination for a *different* web view, never showing one itself.
 * `href` is read straight off the anchor DOM node, which React Router's own `<Link>`/`<NavLink>`
 * always renders as a real `<a href>` — no dependency on which component rendered it.
 */
export function DrawerNavLinkCapture({ children }: { children: ReactNode }) {
  function onClickCapture(event: ReactMouseEvent) {
    // Already handled — the recent-view recorder around it stores the pick first and sends the
    // same `drawerNavigate` itself once stored (F-0414).
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    // External links (none exist in this sidebar today, but never silently swallowed if one is
    // added) and an explicit new-window/new-tab request are left to the browser/web view as usual.
    if (!href || !href.startsWith('/') || href.startsWith('//') || anchor.target === '_blank')
      return;
    event.preventDefault();
    postShellMessage({ type: 'drawerNavigate', path: href });
  }
  // I-1307: `min-w-0`, a flex item of the More panel: the menu is as wide as the panel, never as
  // wide as its longest word. A label keeps its words whole and shrinks (`useWholeWords`); without
  // this the panel widened past the window instead (to 376 of 375 at the largest text size).
  return (
    <div className="min-w-0" onClickCapture={onClickCapture}>
      {children}
    </div>
  );
}

/**
 * The sidebar sliding over the page below 1024px — the iPad in portrait, a phone, a narrow window.
 * A modal dialog on `ModalFrame`, like the app's other overlays (I-0722): opening it moves focus in,
 * Tab stays inside, Escape or a tap beside it closes it, and focus goes back to "Open navigation".
 * It used to leave focus behind the overlay, so a keyboard user who opened it could neither close it
 * with Escape nor reach it with Tab. The frame's layer is hidden from `lg`, where the sidebar is
 * pinned, and its panel is the sheet itself, full height at the left edge.
 */
function SlideOverNavigation({
  onClose,
  children,
}: {
  onClose: () => void;
  /** Given the drawer's own close, which slides it out before `onClose` (I-1262). */
  children: (close: () => void) => ReactNode;
}) {
  const t = useT();
  // I-1262 (AC-18.10): it comes in from the leading edge, its dim on the movement's own timing.
  const motion = useDrawerMotion('leading', onClose);
  return (
    <ModalFrame
      onClose={motion.requestClose}
      label={t('navigation.sidebar.navigation')}
      // I-2459: above the docked bar's place (`DockedBar`, z-40), which a sheet covers: the bar
      // (a customer's "Approve 1 design") lay over the drawer's last rows, Sign out under it. Below
      // a dialog the drawer opens (z-50).
      layerClassName="z-[45] lg:hidden"
      backdropClassName="bg-black/30"
      // I-1307: the page's sliver beside the drawer is 32 px, in px. As `2rem` it was 100 px at
      // the largest text size, and the drawer 290 of a phone's 390.
      panelClassName="mobile-drawer relative flex h-full w-[min(22rem,calc(100vw-32px))] bg-[#F1F2F2] shadow-xl dark:border-r dark:border-white/10 dark:bg-[#171717]"
      data={{ 'touch-targets': '', 'drawer-phase': motion.phase }}
      motion={{ backdrop: motion.backdropStyle, panel: motion.panelStyle }}
    >
      {children(motion.requestClose)}
    </ModalFrame>
  );
}

/**
 * What the narrow header calls the current page.
 *
 * ⚠️ **The fallback is `'Home'`, so a route missing from this list does not look unlabelled — it
 * claims to be Home.** `/infrastructure` was missing and had been showing "Home" on every narrow
 * viewport; `/agents` would have joined it. `documentTitle.test.ts` now walks the routes in
 * `App.tsx` and fails on any this cannot name, because the next route added will be forgotten here
 * too and nothing about the diff would look wrong.
 */
export function viewTitleForPath(
  t: ChromeTranslateFn,
  pathname: string,
  pageTitle?: string,
): string {
  // AC-18.5: the bar's title is `barTitleOf`'s, the one function both bars read; this is its title,
  // in the reader's language (T-0838).
  return chromeLabelText(barTitleOf(pathname, { pageTitle }).label, t);
}

/**
 * T-0838 (IN-27): the bar's title for the screen on display, in the reader's language: what the
 * narrow header draws and what the `screen` message sends the iPhone's native header.
 */
export function useViewTitle(pathname: string, options: IBarTitleOptions): string {
  const t = useT();
  return chromeLabelText(barTitleOf(pathname, options).label, t);
}

interface AppSidebarProps {
  /**
   * The work area the address on screen is about, when the screen is not a page (`addressFrame`):
   * a work area, `null` for none, `undefined` to name the selection (I-0793).
   */
  addressWorkArea?: IProject | null;
  onNavigate: () => void;
  onCreateProject: (namespaceId?: string | null) => void;
  onProjectSelect: (projectId: string) => void;
  onWorkAreaOverview: () => void;
  /** Scopes the app to one namespace's whole subtree (F-0383, §10.9). */
  onSelectNamespace: (namespaceId: string) => void;
  onSignOut: () => void;
  onSignIn: () => void;
  onClose?: () => void;
  /**
   * F-0413 (AC-18.3): `phone` is the More menu laid out for a finger. The default is the sidebar as
   * the desktop, the iPad and a browser from 768 px draw it.
   */
  layout?: SidebarLayoutType;
  /**
   * I-1170 (AC-FB23): a phone browser's drawer draws Send Feedback as its first row. Only that
   * drawer: the native drawer's embedded panel draws none (FD-5), and the More screen has its own.
   */
  offersFeedback?: boolean;
  /**
   * I-1304 (IL-17): the iPad app's Back and Forward, in a row of their own at the top of the
   * sidebar, above the work-area picker. Only the pinned sidebar (1024 and wider) draws them; below
   * that they are in the narrow header beside the sidebar button.
   */
  historyNav?: boolean;
}

type SidebarLayoutType = 'sidebar' | 'phone';

/** Exported for its render test (`Layout.guestSidebar.test.tsx`); the app draws it through `Layout`. */
export const AppSidebar = observer(function AppSidebar({
  layout = 'sidebar',
  addressWorkArea,
  onNavigate,
  onCreateProject,
  onProjectSelect,
  onWorkAreaOverview,
  onSelectNamespace,
  onSignOut,
  onSignIn,
  onClose,
  offersFeedback = false,
  historyNav = false,
}: AppSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    projectStore,
    authStore,
    dashboardPageStore,
    decisionsStore,
    tasksStore,
    notificationStore,
  } = useStores();
  const namespaceOf = (workAreaId: string) => projectStore.projectMap?.[workAreaId]?.namespaceId;
  const t = useT();
  // `null` means "not creating"; a string or `''` means creating under that page — `''` being the
  // top level, which is what + beside Home makes.
  const [createPageParentId, setCreatePageParentId] = useState<string | null>(null);
  // KD-16a: the New page dialog loads on demand, so the keyboard is held in the tap that asks for it.
  const openNewPage = (parentId: string) => {
    holdKeyboardForDialog();
    setCreatePageParentId(parentId);
  };
  const sidebarColumn = useRef<HTMLDivElement | null>(null);
  const sidebarFooter = useRef<HTMLDivElement | null>(null);
  const oneScroller = useOneSidebarScroller(sidebarColumn, sidebarFooter);
  // F-0579 ACC-4e: the walks are drawn again whenever what they draw changes, a fold included.
  useLayoutEffect(() => installAccentWalks(), []);
  /*
    I-1308 reopened (CFC-f6c): with a finger at 1024 × 768 the upper part showed 430 of its 712 px and
    nothing said the Views group lay below. The part that scrolls fades its bottom edge while more of
    it is below, as I-1450's capped bars do: the list itself, or the whole column where the footer
    scrolls with it (CFC-f6b). A line reached by the keyboard stops above the fade (`scroll-pb-10`, the
    fade's height).
  */
  const sidebarFade = useBottomScrollFade<HTMLDivElement>();
  const fadeSidebar = sidebarFade.ref;
  useLayoutEffect(() => {
    const column = sidebarColumn.current;
    fadeSidebar(oneScroller ? column : (column?.firstElementChild as HTMLDivElement | null));
  }, [oneScroller, fadeSidebar]);
  const sidebarEdge = (
    <BottomScrollFade
      overflowing={sidebarFade.overflowing}
      moreBelow={sidebarFade.moreBelow}
      fromClassName="from-[#F1F2F2] dark:from-[#171717]"
      insetClassName="-inset-x-[12px] -bottom-[12px]"
      placementClassName="shrink-0"
      // F-0519 (IP-46, I-1659): a shade, not the background over the last row.
      tone="shadow"
    />
  );
  const user = authStore.user;
  // ⭐ The scope, not the URL (I-0405): Agents under All work areas is still All work areas.
  // The same rule the tab title reads, so the badge and the tab cannot be about different scopes.
  // RD-24: except while the address is a tab root on its way to the app's scope (it names the scope
  // left), so a relayed pick moves each badge store once, not there and back.
  const overviewMode = addressFollowsAppScope(
    location.pathname,
    projectStore.appScope,
    projectStore.projects,
    appScopeFollowingFrom(),
  )
    ? projectStore.allWorkAreas
    : isAllWorkAreasScope(location.pathname, projectStore.allWorkAreas);
  // I-0480: the badge needs the set whether or not the decisions page is open.
  // I-0560 (AC-5): and it needs the set for the scope the reader is IN — every work area under All,
  // the selected one alone otherwise — so the number and the list beneath it cannot disagree.
  /*
    I-0793: the work area the sidebar's lines lead into is the one the switcher names — the address
    frame's over a state that is not a page, the selection otherwise. At `/acme` the switcher read
    "No work area selected" while Work items, Roadmap and the rest still led into the work area
    visited last. With no work area at the address, those lines are not drawn (`noWorkAreaHere`).

    F-0383, §10.9: a namespace scope keeps that same last work area behind it (ProjectStore.
    selectedNamespaceId, like allWorkAreas), so this fallback must not hand it back here either —
    the namespace's own sidebar lines are the routing work that follows this click wiring, not part
    of it, but showing the work area left behind as though it were still the scope is a correctness
    bug this wiring would otherwise introduce.
  */
  const sidebarWorkArea =
    addressWorkArea !== undefined
      ? addressWorkArea
      : projectStore.selectedNamespaceId
        ? null
        : projectStore.selectedProject;
  // F-0788 (PJ-10e): a namespace's page names the namespace in the selector, so the line saying the
  // address names no work area would contradict the page under it; nothing is drawn there instead.
  const namespacePageHere = useViewedPublicWorkArea()?.id.startsWith('namespace:') ?? false;
  const noWorkAreaHere = addressWorkArea === null;
  /*
    Batch 485: before the work areas are answered the scope's lines are not drawn at all. Drawn with
    no work area, Home, Activity, Work items and Roadmap stood alone for a frame, and the Pages header,
    Decisions, Tasks, Parts and Settings arriving with the work area pushed them 30–90 px down (and
    Agents 30 px) under a reader's eyes. Drawn once, they never move. A failed discovery draws them
    as before, so a sidebar with no answer coming is never left empty.
  */
  const scopeLinesPending =
    !overviewMode &&
    !projectStore.selectedNamespaceId &&
    !noWorkAreaHere &&
    !projectStore.hasWorkAreaAnswer &&
    !projectStore.error;
  const selectedWorkAreaId = sidebarWorkArea?.id ?? null;
  // F-0621 (AGM-41): the reader is a guest here — a guest role or a page share, nothing else — so the
  // sidebar draws the pages shared with them and Shared with you, and none of the members' lines.
  const guestHere =
    !overviewMode && !projectStore.selectedNamespaceId && isGuestWorkArea(sidebarWorkArea);
  const namespaceScopeId = projectStore.selectedNamespaceId;
  // The waiting decisions are read by `useDecisionsWaitingRead`, which `Layout` runs at every width.
  // F-0271 RL-5e: a guest — invited to a work area to read what is shared with them — is a member of
  // no work area, so the app's home would be an empty list. They land on what is shared instead.
  const sharedWithYouCount = useSharedWithYouCount();
  // F-0461 (OF-31): "My ideas" in the sidebar for a reader who reported any.
  const myIdeasCount = useMyIdeasCount();
  // RL-5e and AGM-41: a guest everywhere, or a person with nothing listed and something shared,
  // starts on Shared with you rather than an empty app home.
  // The landing and the account rows (PX-1) ask every listed work area, a guest's too, one question:
  // is the reader a guest everywhere. One read answers both (AGM-51's register names it).
  const listedWorkAreas = projectStore.projects;
  const landOnShared = landsOnSharedWithYou({
    pathname: location.pathname,
    hasWorkAreaAnswer: projectStore.hasWorkAreaAnswer,
    workAreas: listedWorkAreas,
    sharedWithYouCount,
  });
  useEffect(() => {
    if (landOnShared) navigate(SHARED_WITH_YOU_PATH, { replace: true });
  }, [landOnShared, navigate]);
  // I-0900: the "Tasks for me" badge needs the set whether or not its page is open, same as Decisions.
  useEffect(() => {
    if (overviewMode) void tasksStore.loadAllWorkAreas();
    else if (namespaceScopeId) void tasksStore.loadNamespace(namespaceScopeId);
    else if (selectedWorkAreaId && !guestHere) void tasksStore.load(selectedWorkAreaId);
  }, [overviewMode, namespaceScopeId, selectedWorkAreaId, guestHere, tasksStore]);
  // F-0321: *Parts* joins the navigation once the work area has an active part — the server's flag.
  const selectedWorkAreaHasParts = useWorkAreaHasParts(overviewMode ? null : selectedWorkAreaId);
  const selectedRouteRef = workAreaRouteRefOf(sidebarWorkArea);
  const homePath = projectHomePath(selectedRouteRef);
  const membersOnlyHere = (name: string) => t('navigation.membersOnlyHere', { name });
  /*
    D-0374 a (Jay, 2026-10-22): a guest everywhere, the seed Personal work area not counting (F-0737
    PW-R8, PX-1), as the landing and the app's bar read it, is shown no Conversations or Agents.
    `memberWorkAreas` alone counted that Personal as membership. PX-18 (D-0432 b, Jay, 2026-10-29):
    this work area's own team controls are left out for a guest reader here, a member elsewhere
    included; a member of this work area missing a permission still sees them disabled.
  */
  const guestOnly = isGuestOnlyReader({
    hasWorkAreaAnswer: projectStore.hasWorkAreaAnswer,
    workAreas: listedWorkAreas,
    sharedWithYouCount,
  });
  const guestHides = guestHere;
  const accountItemsContext = sidebarAccountItemsContextFor({
    layout,
    inShell: isShellRuntime(),
  });
  /*
    F-0415 (AC-18.3): the lower part's destinations are one table, read by this sidebar and by the
    phone menu alike — see `sidebarAccountItems`.
  */
  const accountItems = sidebarAccountItems({
    schedulesPath: schedulesPathFor({
      aggregate: overviewMode || Boolean(namespaceScopeId),
      allWorkAreas: overviewMode && !namespaceScopeId,
      selectedWorkAreaSlug: selectedRouteRef,
    }),
    guestWorkArea: guestHere ? { refusedReason: membersOnlyHere } : null,
    signedIn: Boolean(user),
    context: accountItemsContext,
    t,
    guestOnly,
    // I-2453: what a phone browser's bottom bar already shows is dropped by `PhoneMenu`, which reads
    // the bar in the phone chunk (`dropsBarItems`), so no desktop screen reads the bar at all.
  });
  const accountItem = (id: (typeof accountItems)[number]['id']) =>
    accountItems.find((item) => item.id === id);
  const phone = layout === 'phone';
  // I-2453: the phone menu is drawn from the narrow window's chunk (`loadPhoneChrome.ts`).
  const phoneShell = usePhoneChromeModule(phone);
  const PhoneMenu = phoneShell?.PhoneMenu;
  const PhoneFeedbackRow = phoneShell?.PhoneFeedbackRow;
  const RecentViewRecorder = phoneShell?.RecentViewRecorder;
  // The same lines in both layouts; a phone draws each as a 44 px row with a larger icon.
  const linkClasses = phone ? phoneRowClasses : navLinkClasses;
  const lineIcon = phone ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0';
  // AC-16 (`concept-pages-and-documents.md` rule 12): a tree row's icon follows its text — on the
  // phone, where the rows' titles are the lines' size, the line's own; on a desktop the tree's 14 px.
  const rowIcon = phone ? 'h-5 w-5' : 'h-3.5 w-3.5';
  // I-1596: the Views group's header line, as tall as `navLinkClasses` (h-7) or a touch target.
  const viewsReserveClass = phone ? 'min-h-touch' : 'min-h-7 [@media(pointer:coarse)]:min-h-touch';
  /*
    Which lines the sidebar has, and where each points, is one table (`sidebarNavItems`) rather than
    three branches here — see that file for why I-0560 made it one. The badge is built here because
    the number on it belongs to this store, and the scope it counts has to be the scope on screen.
  */
  // I-0480: the number waiting for this reader here, from the store the page reads too. I-0611
  // (AC-5): and through the rule the browser tab reads, so the badge and the tab are one number.
  const decisionsScope = {
    allWorkAreas: overviewMode,
    workAreaId: selectedWorkAreaId,
    namespaceId: namespaceScopeId,
  };
  // I-0900: the same scope object works for Tasks — both stores key their set the same way.
  const tasksScope = decisionsScope;
  // F-0381 (BX-3, revised): the expanded box's height, remembered per person on this device,
  // clamped against a maximum measured from this sidebar's own scroll column (`containerRef`
  // below) rather than a viewport formula — see `useResizableBoxHeight.ts`'s module doc.
  const boxResize = useResizableBoxHeight(authStore?.user?.id ?? null);
  const navItems = sidebarNavItems({
    phoneMore: layout === 'phone',
    overviewMode,
    namespaceId: namespaceScopeId,
    selectedWorkAreaSlug: selectedRouteRef,
    hasParts: selectedWorkAreaHasParts === true,
    sharedWithYou: (sharedWithYouCount ?? 0) > 0,
    guest: guestHere,
    myIdeas: (myIdeasCount ?? 0) > 0,
    // F-0744 (AC-NB5): the notices each list draws are in its one badge, never a second one.
    decisionsBadge: (
      <DecisionsWaitingBadge
        count={withNotices(
          decisionsWaitingInScope(decisionsStore, decisionsScope),
          noticesWaitingInScope(notificationStore, 'decision', decisionsScope, namespaceOf),
        )}
        failed={decisionsWaitingUnknownInScope(decisionsStore, decisionsScope)}
      />
    ),
    tasksBadge: (
      <TasksWaitingBadge
        count={withNotices(
          tasksWaitingInScope(tasksStore, tasksScope),
          noticesWaitingInScope(notificationStore, 'task', tasksScope, namespaceOf),
        )}
        urgent={tasksUrgentInScope(tasksStore, tasksScope)}
        failed={tasksWaitingUnknownInScope(tasksStore, tasksScope)}
      />
    ),
  });
  const projectId =
    addressWorkArea !== undefined ? (addressWorkArea?.id ?? null) : projectStore.selectedProjectId;
  // The server's order, as served (F-0186, P-4). The sidebar used to re-sort by position and then
  // by title, which broke ties differently from every other reader.
  //
  // F-0383, §10.9: a namespace scope is neither `overviewMode` (that is All work areas) nor a
  // single work area, so it needs its own guard here too, the same reason `sidebarWorkArea`
  // already stopped handing back the work area left behind — otherwise this reads `projectId`'s
  // raw fallback (the last-selected single work area) and draws that work area's own page tree
  // under a namespace's Home link.
  const dashboardPages =
    overviewMode || namespaceScopeId
      ? []
      : dashboardPageStore.pages(projectId).filter((page) => !page.isHome);

  useEffect(() => {
    if (projectId && !overviewMode && !namespaceScopeId)
      dashboardPageStore.ensureProject(projectId);
  }, [dashboardPageStore, overviewMode, namespaceScopeId, projectId]);

  async function createPage(title: string): Promise<void> {
    const project = sidebarWorkArea;
    if (!project) return;
    const created = await dashboardPageStore.createPage(
      project.id,
      title,
      createPageParentId || null,
    );
    setCreatePageParentId(null);
    onNavigate();
    // Navigating to the new page is what opens its parent: the tree always shows the page being
    // read, so a child created inside a collapsed branch opens that branch (F-0201, AC-8).
    navigate(projectDashboardPageEditPath(workAreaRouteRef(project), created.id), {
      state: dashboardPageEditNavigationState(created.id),
    });
  }

  // I-1817 (D-0305 c): Home is the first-level line in every scope. Under one work area its pages
  // are its children, folding from Home itself; All work areas and a namespace have none.
  const homeHasPagesHere = Boolean(projectId && !overviewMode && !namespaceScopeId);
  const homeTo = sidebarHomePath({ overviewMode, namespaceId: namespaceScopeId, homePath });
  const homeIcon = <FeatItIcon className={phone ? 'h-5 w-5' : 'h-4 w-4'} variant="theme" />;
  const homeLink = (
    <NavLink
      end
      to={homeTo}
      className={({ isActive }) => `${linkClasses({ isActive })} min-w-0 flex-1`}
      onClick={onNavigate}
    >
      {homeIcon}
      <span className="truncate">{t('navigation.home')}</span>
    </NavLink>
  );

  const primary = scopeLinesPending ? (
    <nav aria-label={t('navigation.sidebar.primary')} className="space-y-0.5" />
  ) : noWorkAreaHere && namespacePageHere ? (
    <nav aria-label={t('navigation.sidebar.primary')} className="space-y-0.5" />
  ) : noWorkAreaHere ? (
    <p
      data-testid="sidebar-no-work-area"
      className="px-2 py-1 text-[12px] leading-5 text-gray-500 dark:text-gray-400"
    >
      {t('navigation.sidebar.noWorkAreaHere')}
    </p>
  ) : (
    <nav aria-label={t('navigation.sidebar.primary')} className="space-y-0.5">
      {homeHasPagesHere && projectId ? (
        /*
          I-1817 (D-0305 c, Jay: "first home then on next levels custom pages", "just collapsible"):
          no "Pages" header. The work area's pages are Home's children, one level in, and fold from
          Home's own icon place; New page sits beside Home.
        */
        <HomeNavigationGroup
          workAreaId={projectId}
          homeTo={homeTo}
          homeActive={matchPath({ path: homeTo, end: true }, location.pathname) !== null}
          pageOpen={dashboardPageIdFromPath(location.pathname) !== null}
          hasPages={dashboardPages.length > 0}
          icon={homeIcon}
          linkClassName={linkClasses}
          lineIconClassName={lineIcon}
          onNavigate={onNavigate}
          // PX-18 (D-0427 a): a guest's frame offers only what their grants allow, so New page is
          // drawn for a guest who may manage pages and for no other; a member who may not manage
          // pages sees it disabled, with the reason.
          actions={
            guestHere && !guestFrameOffers(sidebarWorkArea, 'work_area.manage') ? null : (
              <SidebarNewPageButton
                manageAnswer={dashboardPageStore.manageAnswer(projectId)}
                manageRead={dashboardPageStore.manageRead(projectId)}
                phone={phone}
                onOpen={() => openNewPage('')}
              />
            )
          }
        >
          <PageNavigationList
            pages={dashboardPages}
            workAreaId={projectId}
            projectSlug={selectedRouteRef}
            currentPageId={dashboardPageIdFromPath(location.pathname)}
            canManage={dashboardPageStore.manageAnswer(projectId)}
            manageRead={dashboardPageStore.manageRead(projectId)}
            manageControls={!guestHides || guestFrameOffers(sidebarWorkArea, 'work_area.manage')}
            workArea={sidebarWorkArea}
            reordering={dashboardPageStore.isReordering(projectId)}
            orderError={dashboardPageStore.orderError(projectId)}
            onReorder={(pageIds, parentPageId) =>
              void dashboardPageStore.reorderPages(projectId, pageIds, parentPageId)
            }
            onMove={(pageId, parentPageId) =>
              void dashboardPageStore.movePage(projectId, pageId, parentPageId)
            }
            onCreateChild={(parentPageId) => openNewPage(parentPageId)}
            onDismissOrderError={() => dashboardPageStore.dismissOrderError(projectId)}
            onNavigate={onNavigate}
            linkClassName={linkClasses}
            rowIconClassName={rowIcon}
          />
        </HomeNavigationGroup>
      ) : (
        homeLink
      )}
      {navItems.map((item, index) => {
        const Icon = item.icon;
        // Keyed by the line, not by where it points: the sidebar draws before the work area is
        // known, and a key on `to` remounted every line when it arrived — a link someone was
        // focused on vanished under them, and one being measured read 0 × 0.
        return (
          <NavLink
            key={item.label}
            to={item.to}
            className={linkClasses}
            onClick={onNavigate}
            data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {/* F-0579 ACC-4: Home is the line above, so the walk starts one stop in. */}
            <Icon className={lineIcon} {...accentIconProps(index + 1)} />
            {/* I-1925: the line's English name is its identity; it is drawn in the reader's language. */}
            <span className="min-w-0 flex-1 truncate">{pageName(t, item.label)}</span>
            {'badge' in item ? item.badge : null}
          </NavLink>
        );
      })}
      {/*
          F-0363: the work area's views, as their own expandable/collapsible group — not a flat
          line to a second column any more. Only under one work area; the All-work-areas scope
          keeps its own "Views" line two lines up (`navItems`, `WORK_AREAS_VIEWS_PATH`), which
          still opens `WorkAreasViewsPage`'s own catalog.
        */}
      {/*
          I-1596: the group stands in a wrapper that holds its header line from the first paint, and
          the tree box's maximum counts it as that line and no more (`reserveExcess` in the hook), so the
          chunk and the rows arriving later move nothing above them.
        */}
      {/*
          AGM-41: the views are a members' read the server refuses a guest, so a guest's work area
          draws no Views line (PX-18, D-0432 b), whatever the reader holds elsewhere.
        */}
      {!overviewMode && !guestHere && sidebarWorkArea && selectedRouteRef ? (
        <div data-box-reserve="" className={viewsReserveClass}>
          <ViewNavigationList
            workArea={sidebarWorkArea}
            projectSlug={selectedRouteRef}
            onNavigate={onNavigate}
            linkClassName={linkClasses}
            lineIconClassName={lineIcon}
            rowIconClassName={rowIcon}
            // F-0579 ACC-4a: the line after the primary lines, which walk from Home's place.
            accentPosition={navItems.length + 1}
          />
        </div>
      ) : null}
      {overviewMode && !namespaceScopeId ? (
        <div data-box-reserve="" className={viewsReserveClass}>
          <AllWorkAreasViewsList
            // AGM-51: the views of the reader's member work areas, never a guest's.
            workAreas={memberWorkAreas(projectStore.projects)}
            onNavigate={onNavigate}
            linkClassName={linkClasses}
            lineIconClassName={lineIcon}
            rowIconClassName={rowIcon}
            accentPosition={navItems.length + 1}
          />
        </div>
      ) : null}
    </nav>
  );

  const newPageDialog =
    createPageParentId !== null ? (
      <NewDashboardPageDialog
        parentTitle={
          dashboardPageStore.pages(projectId).find((page) => page.id === createPageParentId)
            ?.title ?? null
        }
        onClose={() => setCreatePageParentId(null)}
        onCreate={createPage}
      />
    ) : null;

  if (phone) {
    /*
      F-0415 (AC-18.3). One scroll box for the whole menu: a pinned lower part of seven 44 px
      rows would leave a phone's drawer a third of its height for everything else. Three plain
      headings, and under the last one every control is a row with its name.
    */
    const menu = (
      <div className="flex min-h-0 w-full flex-col" data-phone-navigation="">
        {PhoneMenu ? (
          <PhoneMenu
            guest={guestHere}
            header={
              onClose ? (
                <div className="flex min-h-touch items-center justify-between pl-3">
                  <span className="text-[16px] font-semibold text-gray-950 dark:text-white">
                    {t('navigation.sidebar.menu')}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('navigation.sidebar.closeNavigation')}
                    title={t('navigation.sidebar.closeNavigation')}
                    className="h-touch w-touch px-0"
                    onClick={onClose}
                  >
                    <X className="h-5 w-5 touch-glyph" />
                  </Button>
                </div>
              ) : null
            }
            notice={<DeploymentUpdateRow reserve={false} />}
            workArea={
              <ProjectSelector
                presentation="phone"
                addressWorkArea={addressWorkArea}
                onCreateProject={onCreateProject}
                onProjectSelect={onProjectSelect}
                onWorkAreaOverview={onWorkAreaOverview}
                onSelectNamespace={onSelectNamespace}
                overviewMode={overviewMode}
                boxResize={boxResize}
                onClose={onClose}
              />
            }
            primary={primary}
            accountItems={accountItems}
            dropsBarItems={accountItemsContext === 'mobile-safari'}
            user={user}
            onNavigate={onNavigate}
            offersDesktopDownload={!isShellRuntime()}
            feedback={
              /*
                I-1328: the app's drawer under `drawerInWeb` has no native Send feedback above this
                panel, so the panel offers it and the shell runs it over the content screen (FD-2).
              */
              // F-0758 (PX-18, D-0432 b): a guest's phone frame draws Send feedback only where their
              // grants let them create, as the desktop frame does; a member keeps it disabled with why.
              PhoneFeedbackRow &&
              (offersFeedback || accountItemsContext === 'app-drawer-web') &&
              sidebarWorkArea &&
              (!guestHere || guestFrameOffers(sidebarWorkArea, 'work_item.create')) ? (
                <PhoneFeedbackRow
                  gate={workAreaGate(sidebarWorkArea, 'work_item.create')}
                  onSend={() => {
                    if (accountItemsContext === 'app-drawer-web') {
                      postShellMessage({ type: 'drawerFeedback' });
                      return;
                    }
                    onNavigate();
                    openFeedbackSheet();
                  }}
                />
              ) : null
            }
            onSignOut={onSignOut}
            onSignIn={onSignIn}
          />
        ) : null}
        {newPageDialog}
      </div>
    );
    return RecentViewRecorder ? <RecentViewRecorder>{menu}</RecentViewRecorder> : menu;
  }

  const profileItem = accountItem('profile');

  return (
    // I-1308 (CFC-f6): with a coarse pointer every control of the sidebar is a 44 px target, by the
    // one rule (`index.css`); a fine pointer reads nothing from this.
    // I-1308 (CFC-f6b): where the pinned footer would leave the list less than half the sidebar (the
    // largest text size), the two are one scroller: this one, and the footer follows the list.
    <div
      ref={sidebarColumn}
      className={`flex min-h-0 w-full flex-col px-3 py-3 ${oneScroller ? 'scroll-pb-10 overflow-y-auto overscroll-contain' : ''}`}
      data-coarse-targets="sidebar"
      data-one-scroller={oneScroller ? '' : undefined}
      // F-0579 ACC-4e: every icon the sidebar draws is one gradient, but the footer row's own.
      data-accent-walk="sidebar"
    >
      {/*
        Only this part scrolls (I-0395, I-0401): a long page list can be reached, and the footer —
        Settings, the bell, the theme — stays where a person expects it. The negative margin gives
        focus rings the gutter back that the scroll box would otherwise clip. `relative` keeps an
        absolutely positioned descendant inside this scroll box rather than in the document (I-0412).
        F-0381 (BX-3, revised again): also what `boxResize`'s `othersHeightOf` measures against —
        the `<nav>` lines below `ProjectSelector`, and the header row/resize handle *inside* it,
        are read directly as "the layout's other children" (never from the box's own `offsetHeight`
        or from this div's `scrollHeight`), so a line added to either `<nav>` shrinks the box's
        maximum automatically, with no separate constant to keep in sync. `ProjectSelector`'s own
        wrapper `<section>` is a direct child here, but `boxResize.boxRef` attaches several levels
        *inside* it — `othersHeightOf` walks that ancestor chain rather than assuming `box` is a
        direct child of this div.
      */}
      <div
        ref={boxResize.containerRef}
        className={`relative -mx-3 px-3 pb-3 ${oneScroller ? 'shrink-0' : 'min-h-0 flex-1 scroll-pb-10 overflow-y-auto overscroll-contain'}`}
      >
        <ProjectSelector
          addressWorkArea={addressWorkArea}
          onCreateProject={onCreateProject}
          onProjectSelect={onProjectSelect}
          onWorkAreaOverview={onWorkAreaOverview}
          onSelectNamespace={onSelectNamespace}
          overviewMode={overviewMode}
          boxResize={boxResize}
          onClose={onClose}
          leading={historyNav ? <ShellHistoryNav /> : undefined}
        />

        {primary}
        {oneScroller ? null : sidebarEdge}
      </div>

      <div ref={sidebarFooter} className="shrink-0 space-y-3">
        {/*
          I-0781 (QA 267): where "FEATit was updated" is said and acted on. It sits over no control
          because it is a row of its own rather than a card over the page.
          F-0519 (IP-45, I-1660): it takes no room while it has nothing to say. It was held at 40 px,
          which left an empty band above Agents on the desktop and the iPad. This footer is anchored
          to the bottom of the sidebar, so when the row appears the rows below it stay where they are
          and the list above gives it its room.
        */}
        <DeploymentUpdateRow reserve={false} />
        <nav aria-label={t('navigation.sidebar.configuration')} className="space-y-0.5">
          {/*
            Agents belongs to the person, not to a work area, and the same list is the same list
            whichever work area happens to be selected — unlike Infrastructure, which left this rail
            for an icon in the footer below (U-0007): it is global rather than work-area specific,
            and Jay asked for it beside Settings and the bell rather than filling a line up here.
          */}
          {/* F-0415: read from `sidebarAccountItems`, the table the phone menu reads too. */}
          {accountItems
            .filter((item) => !scopeLinesPending && item.desktop === 'row' && item.id !== 'profile')
            .map((item, index) => {
              const Icon = item.icon;
              const line = (
                <>
                  {/* F-0579 ACC-4a: a list of its own, walking the stops from its top. */}
                  <Icon className="h-4 w-4 shrink-0" {...accentIconProps(index)} />
                  <span className="truncate">{item.label}</span>
                </>
              );
              return (
                <NavLink key={item.id} to={item.to} className={navLinkClasses} onClick={onNavigate}>
                  {line}
                </NavLink>
              );
            })}
        </nav>

        {/*
          U-0024 (IP-30), F-0777: with a finger (`index.css`, `[data-sidebar-account]`) the box is
          one line of four 44 px cells: the person, Send feedback, the deployment switch or Download,
          and Sign out. Four 44 px targets 8 px apart are 200 px, the box's width at 236.
        */}
        <div
          data-sidebar-account=""
          // F-0579 ACC-4e: the footer's icons are a walk of their own, left to right.
          data-accent-walk="footer"
          className="space-y-3 rounded-md bg-[#FEFEFE]/60 p-1.5 dark:bg-[#212121]/70"
        >
          {/*
            Wraps rather than running off the edge at the largest text sizes (T-0318), and fits one
            line at the sidebar's narrowest with every action it can draw (F-0585 PSW-7, QA 0520):
            the person's avatar (F-0777), Send feedback, Download FEATit or the update, the
            deployment switch and Sign out, each 32 px with no gap between them: 160 px of the 200
            the box has at 236. (F-0744 took Notifications out, F-0774 Settings and Infrastructure.)
          */}
          <div
            data-sidebar-account-actions=""
            className="flex flex-wrap items-center justify-between gap-y-1"
          >
            <div data-sidebar-account-icons="" className="flex flex-wrap items-center gap-y-1">
              {/*
                F-0777 (Jay: "move the profile menu as icon only into the bottom row"): the person is
                the row's first icon, their avatar, named "Profile — <name>"; the line of its own
                that held the avatar and the name is gone. The invitations waiting ride on it
                (I-2233 AC-PI.2, `SidebarProfileLink`).
              */}
              {user && profileItem && (
                <SidebarProfileLink user={user} to={profileItem.to} onNavigate={onNavigate} />
              )}
              {/*
                F-0774 (D-0466, ONE-1): Global settings is no longer a gear here: it is a named row
                above, the one settings entry. The icons below keep their own colour slots (ACC-4b).
              */}
              {/*
                F-0361 (G2, I-0463): the desktop's way to send feedback — never on a phone, whose
                drawer's first row offers it (the native one in the app, `PhoneFeedbackRow` in a
                browser, I-1170), never
                in the drawer's own nav panel (FD-5), and not on the More screen itself, which
                offers its own Send Feedback above this navigation (the iPad's entry).
              */}
              {/* PX-18 (D-0432 b): a guest's frame draws Send feedback only if their grants let them create. */}
              {!phone &&
                sidebarWorkArea &&
                (!guestHere || guestFrameOffers(sidebarWorkArea, 'work_item.create')) &&
                appRouteOf(location.pathname) !== '/more' && (
                  <button
                    type="button"
                    aria-label={t('shell.sendFeedback')}
                    data-feedback-trigger=""
                    onClick={() => {
                      onNavigate?.();
                      openFeedbackSheet();
                    }}
                    {...gateControlProps(workAreaGate(sidebarWorkArea, 'work_item.create'), {
                      title: t('shell.sendFeedback'),
                    })}
                    className="coarse-hit grid h-8 w-8 place-items-center rounded-md text-gray-500 transition-colors hover:bg-[#D7DBDC]/50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:text-gray-400 dark:hover:bg-[#242424] dark:hover:text-gray-100"
                  >
                    <MessageSquarePlus
                      className="h-4 w-4"
                      aria-hidden="true"
                      {...accentIconProps(1)}
                    />
                  </button>
                )}
            </div>
            <div data-sidebar-account-end="" className="flex items-center">
              {/*
                Immediately left of sign-out, and inside the same group so the two stay adjacent
                however the row wraps. It renders nothing at all when this deployment has no build
                for the visitor's platform, so the row is unchanged on a deployment that publishes
                none — which is every deployment until someone cuts a release.
              */}
              {/* T-0320: never an offer to download the desktop app from inside the phone app. */}
              {!isShellRuntime() && <DesktopDownloadButton />}
              {/*
                The same slot, answering the same question from inside the app: not "get it" but
                "there is a newer one". The two are mutually exclusive by construction — each returns
                null in the runtime the other belongs to — so this row never shows both.
              */}
              {isDesktopRuntime() ? (
                <Suspense fallback={null}>
                  <DesktopUpdateButtonChunk />
                  <DesktopCommandFailureNoticeChunk />
                </Suspense>
              ) : null}
              {/* F-0519 (IP-8): the iPad app's deployment switch; nothing anywhere else. */}
              <ShellDeploymentSwitch
                variant="icon"
                className="coarse-hit grid h-8 w-8 place-items-center rounded-md text-gray-500 transition-colors hover:bg-[#D7DBDC]/50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:text-gray-400 dark:hover:bg-[#242424] dark:hover:text-gray-100"
                iconClassName="h-4 w-4"
                accentPosition={2}
              />
              {user ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="coarse-hit w-8 px-0"
                  aria-label={t('navigation.sidebar.signOut')}
                  title={t('navigation.sidebar.signOut')}
                  onClick={onSignOut}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('navigation.sidebar.signInToCloud')}
                  title={t('navigation.sidebar.signInToCloud')}
                  onClick={onSignIn}
                >
                  <LogIn className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {oneScroller ? sidebarEdge : null}

      {newPageDialog}
    </div>
  );
});

/** The phone picker's own region, except in the top row, which is one button in a row. */
function PhoneSelectorFrame({ topRow, children }: { topRow: boolean; children: ReactNode }) {
  const t = useT();
  return topRow ? (
    <>{children}</>
  ) : (
    <section aria-label={t('navigation.sidebar.workAreas')} className="space-y-1.5">
      {children}
    </section>
  );
}

interface ProjectSelectorProps {
  /** See `AppSidebarProps.addressWorkArea`. */
  addressWorkArea?: IProject | null;
  onCreateProject: (namespaceId?: string | null) => void;
  onProjectSelect: (projectId: string) => void;
  onWorkAreaOverview: () => void;
  /** See `AppSidebarProps.onSelectNamespace` (F-0383, §10.9). */
  onSelectNamespace: (namespaceId: string) => void;
  overviewMode: boolean;
  onClose?: () => void;
  /** I-1304 (IL-17): drawn in a row of its own above the picker (`AppSidebarProps.historyNav`). */
  leading?: ReactNode;
  /**
   * F-0415, F-0416 (AC-18.3, AC-18.4): `phone` is one row naming the current work area, which opens
   * the picker sheet in choose mode. The default is the sidebar's own collapsed and expanded forms,
   * with every action directly available, as the desktop and the iPad keep them.
   *
   * IU-3 (S-4): `top-row` is the same picker behind the phone top row's scope button — the work
   * area's own name and ▾ — so the row switches the scope with AC-18.4's sheet, never a second one.
   */
  presentation?: 'sidebar' | 'phone' | 'top-row' | 'edit-page';
  /**
   * F-0381: created in `AppSidebar`, not here — `boxResize.containerRef` has to reach the
   * sidebar's own scroll column (`AppSidebar`'s own div, the box's siblings' home too), which is
   * a level above this component, so the hook lives where that element is and is handed down.
   */
  boxResize: IResizableBoxHeight;
}

export const ProjectSelector = observer(function ProjectSelector({
  presentation = 'sidebar',
  addressWorkArea,
  onCreateProject,
  onProjectSelect,
  onWorkAreaOverview,
  onSelectNamespace,
  overviewMode,
  onClose,
  boxResize,
  leading,
}: ProjectSelectorProps) {
  const t = useT();
  const { projectStore, authStore, namespaceStore } = useStores();
  const location = useLocation();
  const navigate = useNavigate();
  // U-0024 (IP-27): the iPad's sidebar puts the heading's two create controls behind one ＋.
  const touchSidebar = useTouchDesktopLayout();
  // I-0353, AC-U: a verification session's ceiling has no `work_area.create` in it, so the button
  // that would ask for it is disabled before the request, with the reason named — not enabled and
  // left to fail silently against a server that was always going to refuse it.
  //
  // Optional chaining, not `authStore.isVerificationSession`: `ProjectSelector.namespaces.test.tsx`
  // renders this component through `Layout` with a stores mock that provides no `authStore` — no
  // harness of its own exists for it. A missing store is not a verification session, so this reads
  // as `undefined` rather than throwing before that test (or any caller with a partial mock) ever
  // reaches an assertion.
  const createDisabledReason = createWorkAreaRefusal(authStore);
  /*
    SB-3: the namespace heading's own New subgroup/New work area icons used to hide outright for
    this same reason (`undefined` prop, below) while the two other create-work-area entry points in
    this file (just above) already disable-with-reason for it — the inconsistency this gate removes.
    I-1054 (§10.13): `group.canManageHere` is now the real per-namespace `work_area.manage` answer,
    carried from `namespaceTree` itself (`AccessControlService.canManyAt`, batched — never one
    request per row) rather than a stand-in. `everyGate` folds the verification-session check in
    alongside it, so either refusal is shown, never only one silently winning.
  */
  /*
    I-1088 follow-up: the sidebar's own Create work area controls are refused the way every other
    gated control is (`gateControlProps`): dimmed by `[data-gate='refused']`, focusable, the reason
    in the tooltip, and no hover fill. They used `disabled` with `disabled:text-gray-400` over
    `text-gray-500`, which read as enabled.
  */
  const createGate: WorkAreaGateType = createDisabledReason
    ? { state: 'refused', reason: createDisabledReason }
    : { state: 'permitted', basis: 'granted' };
  // I-1733: one note per control — an id names one element — so the refusal is described, not only titled.
  const dropdownCreateRefusal = useRefusalNote(createGate);
  const headerCreateRefusal = useRefusalNote(createGate);
  const phoneCreateRefusal = useRefusalNote(createGate);
  // I-2184: a verification session is read-only, so *New organisation* (a link to Global settings,
  // where the flow starts) is refused before it opens anything, the way Create work area is.
  const organisationGate = createOrganisationGate(authStore);
  const organisationRefused = organisationGate.state === 'refused';
  const createHover =
    createGate.state === 'refused'
      ? ''
      : 'hover:bg-[#E4E6E8] hover:text-gray-950 dark:hover:bg-[#1f1f1f] dark:hover:text-white';

  function namespaceCreateGateFor(group: IWorkAreaNamespaceGroup): WorkAreaGateType {
    return everyGate(
      createDisabledReason
        ? { state: 'refused', reason: createDisabledReason }
        : { state: 'permitted', basis: 'granted' },
      namespaceCreateGate(group),
    );
  }
  const {
    preferences: navigationPreferences,
    setPreferences: setNavigationPreferences,
    saving: navigationSaving,
    error: navigationError,
    save: saveNavigationPreferences,
  } = useWorkAreaNavigationPreferences();
  const [expanded, setExpanded] = useState(initialProjectSelectorExpanded);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // F-0416: the phone picker sheet. Its mode lives in the sheet and is gone when it closes.
  const [sheetOpen, setSheetOpen] = useState(false);
  const phonePicker = presentation === 'phone' || presentation === 'top-row';
  // I-2453: the top row's scope button and the menu's work-area row are drawn from the phone chunk.
  const phoneChrome = usePhoneChromeModule(phonePicker);
  useEffect(
    () => (phonePicker ? whenIdle(() => void WorkAreaPickerSheet.preload()) : undefined),
    [phonePicker],
  );
  const [touchReorderProjectId, setTouchReorderProjectId] = useState<string | null>(null);
  // F-0380: the same touch-reveal state the work-area rows get, scoped to namespace headings.
  const [touchReorderNamespaceKey, setTouchReorderNamespaceKey] = useState<string | null>(null);
  // Which organisation or subgroup an inline "new subgroup" row is open under, if any. An
  // organisation is never created here: that is a deliberate flow in Global settings (I-0682).
  const [creatingNamespaceUnder, setCreatingNamespaceUnder] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownTriggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownPanelRef = useRef<HTMLDivElement | null>(null);
  // I-2400: the row the trigger sits in, with the expand button: the open list covers the sidebar's
  // column, so the Decisions and Tasks badges no longer peek out beside a list as wide as the trigger.
  const dropdownRowRef = useRef<HTMLDivElement | null>(null);
  // Drawn at the document root (I-0401): the sidebar scrolls and would clip it.
  const dropdownStyle = useAnchoredStyle(dropdownRowRef, dropdownPanelRef, dropdownOpen, {
    placement: 'bottom',
    align: 'stretch',
    offset: 4,
  });
  const projects = projectStore.projects;
  const unavailableDataSources = projectStore.unavailableDataSources;
  const workAreaOptionCount = projects.length + unavailableDataSources.length;
  /*
    I-0793: over a state that is not a page the switcher names the address's work area, or none —
    never the last one visited. At `/acme/tracker/-/nope` it read the work area the reader came
    from while the card named Tracker; at `/nosuchns` it named a work area the address never did.
  */
  const selectedProjectId =
    addressWorkArea !== undefined
      ? (addressWorkArea?.id ?? '')
      : (projectStore.selectedProjectId ?? '');
  /*
    F-0383, §10.9: a namespace is the scope, so the work area last visited — kept behind it, the
    same way All work areas keeps it — must not still read as "selected" here. The namespace's own
    row highlighting and the switcher's trigger label are the routing/scope-banner work that follows
    this click wiring (not part of it); this only stops a stale work-area row from looking chosen.
  */
  const inNamespaceScope = Boolean(projectStore.selectedNamespaceId);
  const selectedProject = selectedProjectId
    ? projectStore.projectMap[selectedProjectId]
    : undefined;
  // I-1089: the switcher names the namespace scope as `NamespaceScopeBanner` does.
  const scopedNamespace = inNamespaceScope
    ? namespaceStore?.tree?.find((entry) => entry.id === projectStore.selectedNamespaceId)
    : undefined;
  // F-0739 (PJ-8): a Public work area this reader is not a member of, while its page is drawn.
  const viewedPublic = useViewedPublicWorkArea();
  // PX-33: a customer reads the agency here, or whose pages these are: never the work area's name.
  const guestOfSelected = !!selectedProject && isGuestWorkArea(selectedProject);
  const guestAgency = guestOfSelected ? guestScopeName(selectedProject) : null;
  const guestScope = guestOfSelected
    ? (guestAgency ?? t('navigation.sidebar.guestPages'))
    : undefined;
  const selectorState = {
    // The All scope is a scope of pages; a state that is not one names its own work area, or none.
    overviewMode: overviewMode && addressWorkArea === undefined,
    // Before the tree answers it still is a namespace, not the work area left behind it. I-1109:
    // by its name, as the scope banner and the phone bar name it, not by its address.
    namespaceName:
      inNamespaceScope && addressWorkArea === undefined
        ? scopedNamespace?.name || scopedNamespace?.fullPath || t('navigation.sidebar.namespace')
        : undefined,
    // I-0655, AC-M13: the trigger names the namespace too, `Acme / Tracker`.
    selectedName: selectedProject
      ? (guestScope ?? workAreaQualifiedLabel(selectedProject))
      : undefined,
    viewedPublicName: viewedPublic?.name,
    hasAnswer: projectStore.hasWorkAreaAnswer,
    loading: projectStore.loading,
    optionCount: workAreaOptionCount,
  };
  // PX-1: the switcher shows a guest's work area; its words then never say "work area".
  const guestSelected = !selectorState.overviewMode && isGuestWorkArea(selectedProject);
  /*
    I-2503: the work area whose agency logo has settled, and the one the hold gave up waiting for, by
    the address's reference: the same before the work areas answer and after, so a returning guest's
    hold from the first frame and its give-up carry across the answer.
  */
  const [markSettledFor, setMarkSettledFor] = useState<string | null>(null);
  const [markGaveUpFor, setMarkGaveUpFor] = useState<string | null>(null);
  const markReference = workAreaReferenceOf(location.pathname);
  const markHoldFor =
    markReference ?? (guestSelected ? (selectedProject?.namespaceId ?? null) : null);
  const markHeld = scopeLineHeldForMark({
    guest: guestSelected && !!selectedProject?.namespaceId,
    rememberedMark:
      !selectorState.overviewMode &&
      !selectorState.hasAnswer &&
      projectStore.rememberedNameAt(markReference) !== undefined &&
      projectStore.rememberedMarkAt(markReference),
    holdFor: markHoldFor,
    settledFor: markSettledFor,
    gaveUpFor: markGaveUpFor,
  });
  useEffect(() => {
    if (!markHeld || !markHoldFor) return undefined;
    const timer = setTimeout(() => setMarkGaveUpFor(markHoldFor), SCOPE_MARK_HOLD_MAX_MS);
    return () => clearTimeout(timer);
  }, [markHeld, markHoldFor]);
  const markSettled = useCallback(
    (drawn: boolean) => {
      if (markHoldFor) setMarkSettledFor(markHoldFor);
      projectStore.rememberWorkAreaMark(drawn);
    },
    [markHoldFor, projectStore],
  );
  /*
    I-0655, AC-M12/AC-M13: the list is the namespace tree. Every work area sits under the namespace
    it lives in, the person's own first, and both the collapsed switcher and the expanded list are
    drawn from these groups. ⚠️ `namespaceStore` is optional here: a caller with a partial stores
    mock (and the first render, before the tree answers) still groups, by what each work area says
    about its namespace.
  */
  const namespaceTree = namespaceStore?.tree ?? null;
  const namespaceGroups = useMemo(
    () =>
      groupWorkAreasByNamespace(
        projects,
        namespaceTree,
        navigationPreferences?.workAreaOrder,
        navigationPreferences?.namespaceOrder,
      ),
    [
      namespaceTree,
      navigationPreferences?.workAreaOrder,
      navigationPreferences?.namespaceOrder,
      projects,
    ],
  );
  // F-0380: only an organisation or a subgroup reorders — never the reader's own personal
  // namespace (always first, only ever one of it) or an "other" fallback group (not a namespace
  // the tree actually lists, so not one the reader can move).
  const reorderableNamespaceIds = useMemo(
    () =>
      namespaceGroups
        .filter((group) => group.kind === 'organisation' || group.kind === 'subgroup')
        .map((group) => group.namespaceId as string),
    [namespaceGroups],
  );
  const namespaceNameById = useMemo(
    () => new Map(namespaceGroups.map((group) => [group.namespaceId ?? group.key, group.name])),
    [namespaceGroups],
  );
  // Only while there is no tree to draw: a re-read after a rename keeps the tree on screen.
  const namespaceTreeStatus: 'loading' | 'failed' | null =
    !namespaceStore || namespaceTree !== null
      ? null
      : namespaceStore.failure
        ? 'failed'
        : namespaceStore.loading
          ? 'loading'
          : null;
  /*
    I-2104: "No work areas yet" under a namespace is a statement about the answer, so it waits for
    one: until the work-areas read has answered, and while an empty list is read again. (Before the
    namespace tree answers a group exists only for a work area, so none is empty then: no clause.)
  */
  const workAreasPending =
    projectStore.hasWorkAreaAnswer === false ||
    (projectStore.loading === true && projects.length === 0);
  /*
    The switcher is a picker: a namespace with nothing in it offers nothing to pick, so it is left
    out there. The expanded tree keeps it, because that is where a new work area is created in it.
  */
  const switcherGroups = groupsWithWorkAreas(namespaceGroups);
  useEffect(() => {
    if (namespaceStore && namespaceStore.tree === null && !namespaceStore.loading) {
      void namespaceStore.load();
    }
  }, [namespaceStore]);
  // T-0770 (LC-6a): dnd-kit is a chunk of its own; the rows draw without it until it is in.
  const sortableKit = useSortableKit();

  // Escape, a click outside and focus leaving close it, in the one pop-out order (F-0533, KD-10).
  useDismissableLayer({
    open: dropdownOpen,
    close: () => setDropdownOpen(false),
    closeAndRestoreFocus: () => {
      setDropdownOpen(false);
      dropdownTriggerRef.current?.focus();
    },
    contains: (target) =>
      Boolean(dropdownRef.current?.contains(target) || dropdownPanelRef.current?.contains(target)),
    portalled: { panelRef: dropdownPanelRef, triggerRef: dropdownTriggerRef },
  });
  useEffect(() => {
    if (!dropdownOpen) return;
    // Focus goes into the list: it is drawn at the end of the document (I-0401), so Tab from the
    // trigger would walk the sidebar instead of reaching it.
    const options = dropdownPanelRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    const selected = dropdownPanelRef.current?.querySelector<HTMLElement>(
      '[role="option"][aria-selected="true"]',
    );
    (selected ?? options?.[0])?.focus();
  }, [dropdownOpen]);

  function selectProject(projectId: string): void {
    if (!projectId) return;
    setDropdownOpen(false);
    setSheetOpen(false);
    onProjectSelect(projectId);
  }

  function setSessionExpanded(nextExpanded: boolean): void {
    setDropdownOpen(false);
    setExpanded(nextExpanded);
    if (typeof window !== 'undefined') {
      deviceSession.setItem(PROJECT_SELECTOR_EXPANDED_SESSION_KEY, String(nextExpanded));
    }
  }

  async function handleWorkAreaDragEnd(event: DragEndEvent): Promise<void> {
    if (!navigationPreferences || !event.over) return;
    const nextOrder = moveWorkAreaWithinParent(
      projects,
      navigationPreferences.workAreaOrder,
      String(event.active.id),
      String(event.over.id),
    );
    if (!nextOrder) return;
    const previous = navigationPreferences;
    const next = { ...previous, workAreaOrder: nextOrder };
    setNavigationPreferences(next);
    try {
      await saveNavigationPreferences(next);
    } catch {
      setNavigationPreferences(previous);
    }
  }

  /** F-0380: drag/keyboard reorder for a namespace — the same shape as a work area's above. */
  async function handleNamespaceDragEnd(event: DragEndEvent): Promise<void> {
    if (!navigationPreferences || !event.over || !namespaceTree) return;
    const nextOrder = moveNamespaceWithinParent(
      namespaceTree,
      navigationPreferences.namespaceOrder,
      String(event.active.id),
      String(event.over.id),
    );
    if (!nextOrder) return;
    const previous = navigationPreferences;
    const next = { ...previous, namespaceOrder: nextOrder };
    setNavigationPreferences(next);
    try {
      await saveNavigationPreferences(next);
    } catch {
      setNavigationPreferences(previous);
    }
  }

  /** F-0380: a phone's move up/down control, instead of drag — the same rule, a different trigger. */
  async function handleNamespaceMove(
    namespaceId: string,
    direction: NamespaceMoveDirection,
  ): Promise<void> {
    if (!navigationPreferences || !namespaceTree) return;
    const overId = namespaceMoveTarget(
      namespaceTree,
      navigationPreferences.namespaceOrder,
      namespaceId,
      direction,
    );
    if (!overId) return;
    const nextOrder = moveNamespaceWithinParent(
      namespaceTree,
      navigationPreferences.namespaceOrder,
      namespaceId,
      overId,
    );
    if (!nextOrder) return;
    const previous = navigationPreferences;
    const next = { ...previous, namespaceOrder: nextOrder };
    setNavigationPreferences(next);
    try {
      await saveNavigationPreferences(next);
    } catch {
      setNavigationPreferences(previous);
    }
  }

  /** I-1263 (AC-18.11): a work area's move arrow — the drag's rule (`moveWorkAreaWithinParent`). */
  async function handleWorkAreaMove(overId: string | null, projectId: string): Promise<void> {
    if (!navigationPreferences || !overId) return;
    const nextOrder = moveWorkAreaWithinParent(
      projects,
      navigationPreferences.workAreaOrder,
      projectId,
      overId,
    );
    if (!nextOrder) return;
    const previous = navigationPreferences;
    const next = { ...previous, workAreaOrder: nextOrder };
    setNavigationPreferences(next);
    try {
      await saveNavigationPreferences(next);
    } catch {
      setNavigationPreferences(previous);
    }
  }

  function canMoveNamespace(namespaceId: string, direction: NamespaceMoveDirection): boolean {
    if (!namespaceTree) return false;
    return (
      namespaceMoveTarget(
        namespaceTree,
        navigationPreferences?.namespaceOrder ?? [],
        namespaceId,
        direction,
      ) !== null
    );
  }

  /*
    The namespace tree with everything it offers: reorder handles, the phone's move buttons, the
    per-namespace actions. The expanded sidebar draws it, and so does the phone picker's edit mode
    (F-0416) — one tree, so the two cannot offer different actions. `phoneEdit` shows the handles
    and the actions without hover or a first touch, since the person entered the mode to use them.
  */
  const tree = (phoneEdit: boolean) => {
    const list = (
      <>
        {namespaceTreeStatus && namespaceStore ? (
          <NamespaceTreeStatus
            loading={namespaceTreeStatus === 'loading'}
            failed={namespaceTreeStatus === 'failed'}
            onRetry={() => void namespaceStore.load()}
          />
        ) : null}
        {/*
          F-0380: the namespace layer's own drag/keyboard context, around every heading. ⚠️
          A work area's own `DndContext` below is now per group, not one shared instance
          around the whole list: `useSortable` binds to whichever `DndContext` is its
          *nearest* ancestor, so a single DndContext wrapping both the headings and the rows
          would have bound the headings to the work-area handler instead of this one —
          measured live (the default "Draggable item … was dropped over …" announcement
          firing, and no `onDragEnd` save, because `handleWorkAreaDragEnd` looked up a
          namespace id among work areas and found none). Nesting one group's work-area
          `DndContext` inside this namespace one is still fine: `useSortable` only cares
          about the nearest ancestor, and a group's rows have no other `DndContext` between
          them and their own.
        */}
        <SortableArea
          current={sortableKit}
          ids={reorderableNamespaceIds}
          sensors="pointer"
          onDragEnd={(event) => void handleNamespaceDragEnd(event)}
          announcements={namespaceDragAnnouncements(
            reorderableNamespaceIds,
            (id) => namespaceNameById.get(id) ?? 'namespace',
          )}
        >
          {namespaceGroups.map((group) => (
            <div key={group.key} role="group" aria-label={group.name}>
              <SortableNamespaceHeading
                group={group}
                reorderable={group.kind === 'organisation' || group.kind === 'subgroup'}
                reorderDisabled={!navigationPreferences || navigationSaving}
                touchHandleVisible={phoneEdit || touchReorderNamespaceKey === group.key}
                actionsVisible={phoneEdit}
                onTouchReveal={setTouchReorderNamespaceKey}
                hasNamespaceAbove={
                  Boolean(group.namespaceId) && canMoveNamespace(group.namespaceId as string, 'up')
                }
                hasNamespaceBelow={
                  Boolean(group.namespaceId) &&
                  canMoveNamespace(group.namespaceId as string, 'down')
                }
                onMove={(namespaceId, direction) =>
                  void handleNamespaceMove(namespaceId, direction)
                }
                onCreateWorkArea={
                  group.namespaceId && group.kind !== 'other'
                    ? () => onCreateProject(group.namespaceId)
                    : undefined
                }
                onCreateSubgroup={
                  namespaceStore && (group.kind === 'organisation' || group.kind === 'subgroup')
                    ? () =>
                        setCreatingNamespaceUnder(
                          creatingNamespaceUnder === group.key ? null : group.key,
                        )
                    : undefined
                }
                gate={namespaceCreateGateFor(group)}
                selected={
                  Boolean(group.namespaceId) &&
                  projectStore.selectedNamespaceId === group.namespaceId
                }
                onSelectNamespace={
                  group.namespaceId
                    ? () => onSelectNamespace(group.namespaceId as string)
                    : undefined
                }
              />
              {creatingNamespaceUnder === group.key && namespaceStore && group.namespaceId ? (
                <CreateNamespaceRow
                  depth={group.depth + 1}
                  placeholder={t('navigation.sidebar.newSubgroupPlaceholder')}
                  label={t('navigation.sidebar.newSubgroupName')}
                  onCancel={() => setCreatingNamespaceUnder(null)}
                  onSubmit={async (path) => {
                    await namespaceStore.createSubgroup(group.namespaceId as string, path);
                    setCreatingNamespaceUnder(null);
                  }}
                />
              ) : null}
              {group.rows.length === 0 && !workAreasPending ? (
                <EmptyNamespaceLine depth={group.depth} />
              ) : null}
              <SortableArea
                current={sortableKit}
                ids={group.rows.map(({ project }) => project.id)}
                sensors="pointer"
                onDragEnd={(event) => void handleWorkAreaDragEnd(event)}
              >
                {group.rows.map((row) => (
                  <SortableWorkAreaRow
                    key={row.project.id}
                    row={row}
                    groupDepth={group.depth}
                    selected={
                      !overviewMode && !inNamespaceScope && row.project.id === selectedProjectId
                    }
                    reorderDisabled={!navigationPreferences || navigationSaving}
                    touchHandleVisible={phoneEdit || touchReorderProjectId === row.project.id}
                    comfortable={phoneEdit}
                    onTouchReveal={setTouchReorderProjectId}
                    onSelect={selectProject}
                    moveUpTo={workAreaMoveTarget(group.rows, row.project.id, 'up')}
                    moveDownTo={workAreaMoveTarget(group.rows, row.project.id, 'down')}
                    onMove={(overId) => void handleWorkAreaMove(overId, row.project.id)}
                  />
                ))}
              </SortableArea>
            </div>
          ))}
        </SortableArea>
        {unavailableDataSources.map((descriptor) => (
          <div
            key={descriptor.sourceId}
            title={t('navigation.sidebar.isOffline', {
              name: workAreaDisplayName(descriptor.project),
            })}
            className="flex h-7 w-full cursor-not-allowed items-center gap-2 rounded-md px-2 text-left text-[13px] text-gray-400 dark:text-gray-500"
          >
            <CloudOff className="h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              {workAreaDisplayName(descriptor.project)}
            </span>
            <span className="text-[10px] font-medium uppercase">
              {t('navigation.sidebar.offline')}
            </span>
          </div>
        ))}
      </>
    );
    return (
      <>
        {(workAreaOptionCount > 0 || namespaceGroups.length > 0 || namespaceTreeStatus) &&
          (phoneEdit ? (
            // The picker sheet scrolls as a whole (AC-18.4); the sidebar's resizable box is the
            // sidebar's own.
            <div className="pr-0.5">{list}</div>
          ) : (
            <div>
              {/*
                F-0381: a fixed `max-h-72` (18rem) until now — this is now the person's own saved
                height, clamped to the viewport on every resize. The list inside scrolls on its own
                (`overflow-y-auto`) whenever it is taller than the box, unchanged.
              */}
              <div
                ref={boxResize.boxRef}
                style={{ height: `${boxResize.height}px` }}
                className="overflow-y-auto pr-0.5"
              >
                {list}
              </div>
              <WorkAreaBoxResizeHandle resize={boxResize} />
            </div>
          ))}
        {navigationError ? (
          <p
            role="alert"
            className="px-2 pb-1 text-[11px] leading-4 text-red-600 dark:text-red-300"
          >
            {navigationError}
          </p>
        ) : null}
      </>
    );
  };

  const loadFailure =
    !projectStore.loading && projects.length === 0 && projectStore.error ? (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-md bg-red-50 px-2.5 py-2 text-[12px] text-red-700 dark:bg-red-950/40 dark:text-red-200"
      >
        <span className="min-w-0 flex-1">{projectStore.error}</span>
        <button
          type="button"
          aria-label={t('navigation.sidebar.retryLoadingWorkAreas')}
          title={t('navigation.sidebar.retry')}
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-red-500 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/50"
          onClick={() => void projectStore.fetchProjects()}
        >
          <RotateCw className="h-3.5 w-3.5" />
        </button>
      </div>
    ) : null;
  /*
    lane-loadstuck: a first discovery still in flight names its delay at 10 s, with Try again, as
    every section wait does (WAIT-8), so "Loading work areas" is never an endless wait with no way
    on. At the request timeout the discovery fails and `loadFailure` takes its place.
  */
  /*
    The page may be drawing this wait itself, with its own slow line and Try again (Items under All
    work areas, `allScopeTarget === 'pending'`): then the switcher says only that it is loading, in a
    line, and offers no second button for the same read. Where the page draws no such wait, the
    switcher keeps the slow line, short enough for one line with the link beside it.
  */
  const pageDrawsTheWait =
    projectStore.allWorkAreas &&
    isUnscopedSingleWorkAreaRoute(location.pathname) &&
    allScopeIsPending(
      projectStore.hasWorkAreaAnswer,
      Boolean(projectStore.failure) && !projectStore.loading,
    );
  const loadSlow =
    !projectStore.hasWorkAreaAnswer && projectStore.loading && projects.length === 0 ? (
      <WaitingSlowLine
        onRetry={() => void projectStore.fetchProjects()}
        compact={presentation === 'sidebar'}
        quietWords={pageDrawsTheWait ? t('navigation.picker.loading') : undefined}
        className={
          presentation === 'sidebar' ? 'px-2.5 text-[12px]' : 'flex-wrap px-2.5 text-[12px]'
        }
      />
    ) : null;

  /*
    F-0416 (AC-18.4): edit mode's content — the tree with its reorder, move and create controls and
    the namespace actions. F-0530 (AC-19.4): also the native switcher's Edit view, as a page
    (`edit-page`), so the app has one work-area editor and not two.
  */
  const createRow = phoneRowClasses({ isActive: false });
  const renderPhoneEditor = () => (
    <section aria-label={t('navigation.sidebar.workAreas')} className="space-y-1">
      {/*
            Edit mode's own two actions, as rows with their names (AC-18.3): the sidebar draws
            them as a + and a building in the tree's header. A person who may not create a
            work area gets the row all the same — inert, focusable, and carrying the reason
            as its description, the way every refused control in the app does
            (`gateControl.ts`); no screen writes a reason as text beside a control.
          */}
      <button
        type="button"
        className={`${createRow} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
        {...gateControlProps(createGate, { otherwiseDisabled: projectStore.loading })}
        {...phoneCreateRefusal.controlProps}
        onClick={() => {
          if (createDisabledReason) return;
          setSheetOpen(false);
          onCreateProject();
        }}
      >
        <Plus className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400" />
        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          {t('navigation.sidebar.createWorkArea')}
        </span>
      </button>
      <RefusalDescription id={phoneCreateRefusal.id} reason={phoneCreateRefusal.reason} />
      {namespaceStore ? (
        organisationRefused ? (
          <button
            type="button"
            data-testid="sidebar-organisation-flow-link"
            className={`${createRow} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
            {...gateControlProps(organisationGate)}
          >
            <Building2
              className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 text-left [overflow-wrap:anywhere]">
              <span className="block">{t('navigation.sidebar.newOrganisation')}</span>
              <span className="block text-[13px] font-normal text-gray-500 dark:text-gray-400">
                {t('navigation.sidebar.opensGlobalSettings')}
              </span>
            </span>
          </button>
        ) : (
          <Link
            to={ORGANISATION_FLOW_PATH}
            data-testid="sidebar-organisation-flow-link"
            onClick={() => {
              setSheetOpen(false);
              onClose?.();
            }}
            className={createRow}
          >
            <Building2
              className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
              <span className="block">{t('navigation.sidebar.newOrganisation')}</span>
              <span className="block text-[13px] font-normal text-gray-500 dark:text-gray-400">
                {t('navigation.sidebar.opensGlobalSettings')}
              </span>
            </span>
          </Link>
        )
      ) : null}
      <div className="rounded-lg bg-[#F0F1F2]/70 p-1 dark:bg-[#242424]">{tree(true)}</div>
    </section>
  );

  if (presentation === 'edit-page') return renderPhoneEditor();

  // I-2362: All work areas in the reader's language (`appScopeTitleIn`).
  const appScopeRow = appScopeSwitcherActive()
    ? appScopeTitleIn(
        t,
        appScopeTitleOf(
          location.pathname,
          projectStore.appScope,
          projects,
          projectStore.selectedWorkAreaName,
          projectStore.sessionsOutsideWorkAreas,
        ),
      )
    : null;

  // F-0745 (PX-1): a customer meets the agency by its logo, above the name of their work area.
  const guestLogo =
    guestSelected && selectedProject?.namespaceId ? (
      <OrganisationLogoMark namespaceId={selectedProject.namespaceId} className="mb-1.5 px-1" />
    ) : null;

  if (presentation === 'phone' || presentation === 'top-row') {
    const topRow = presentation === 'top-row';
    const TopRowScopeButton = phoneChrome?.TopRowScopeButton;
    const WorkAreaPickerRow = phoneChrome?.WorkAreaPickerRow;
    /*
      I-1423: before the work areas answer, the name this browser last saw at this very address.
      "Loading work areas ▾" swapped for "Cloud planning ▾" moved the ▾ 37 px at 390.
    */
    const rememberedScopeName = selectorState.hasAnswer
      ? undefined
      : projectStore.rememberedNameAt(workAreaReferenceOf(location.pathname));
    return (
      <PhoneSelectorFrame topRow={topRow}>
        {topRow ? null : guestLogo}
        {topRow && TopRowScopeButton ? (
          <TopRowScopeButton
            mark={
              guestSelected && selectedProject?.namespaceId ? (
                <OrganisationLogoMark
                  namespaceId={selectedProject.namespaceId}
                  size="small"
                  decorative
                  onSettled={markSettled}
                />
              ) : undefined
            }
            // The row names the work area alone; the sheet it opens names its namespace too.
            label={
              selectorState.overviewMode
                ? t('navigation.sidebar.allWorkAreas')
                : ((selectedProject && (guestScope ?? selectedProject.name)) ??
                  rememberedScopeName ??
                  workAreaSelectorLabel(selectorState, t))
            }
            // I-1423: nothing names the work area yet, so the row holds its place without a ▾, and
            // the name and ▾ arrive together where they stay.
            pending={scopeNameIsPending({
              overviewMode: selectorState.overviewMode,
              namespaceName: selectorState.namespaceName,
              hasWorkArea: !!selectedProject,
              hasRememberedName: !!rememberedScopeName,
              hasAnswer: selectorState.hasAnswer,
              loading: selectorState.loading,
              failed: !!(projectStore.failure || projectStore.error),
            })}
            // I-1423: a remembered name is drawn at once; its ▾ waits for the answer that confirms it.
            unconfirmed={
              !selectorState.overviewMode &&
              !selectedProject &&
              !selectorState.hasAnswer &&
              !!rememberedScopeName
            }
            // I-2503: a guest's name and ▾ wait, in their room, for the logo drawn in front of them.
            markHeld={markHeld}
            // The row has no room for the failure, so a failed discovery leaves it open: its sheet
            // names the failure with Retry. Only a first discovery still in flight disables it.
            disabled={workAreaSelectorDisabled(selectorState) && selectorState.loading}
            onOpen={() => setSheetOpen(true)}
          />
        ) : WorkAreaPickerRow ? (
          <WorkAreaPickerRow
            guest={guestSelected}
            // F-0530 (AC-19.2): in the app the row names the app's scope, as the title does. AC-19.13
            // (I-1345): as a picker names it, "Namespace / Work area", with the row's own icon and
            // no badge, the row a browser draws.
            label={appScopeRow?.label ?? workAreaSelectorLabel(selectorState, t)}
            disabled={workAreaSelectorDisabled(selectorState)}
            choosable={!appScopeRow || appScopeRow.choosable}
            // AC-19.11.9 (I-1297): with one work area there is no sheet, so the row leads to the edit
            // view, where Create work area is: the shell opens it as the sheet's Edit does.
            onEdit={
              appScopeRow && !appScopeRow.choosable
                ? () =>
                    postShellMessage({ type: 'drawerNavigate', path: SHELL_WORK_AREA_EDIT_PATH })
                : undefined
            }
            onOpen={() => {
              // F-0530 (AC-19.3): in the app, the drawer's row opens the title's native sheet.
              if (
                appScopeSwitcherActive() &&
                openWorkAreaSwitcher(
                  projectStore,
                  location.pathname,
                  orderedWorkAreas(namespaceGroups),
                )
              ) {
                return;
              }
              setSheetOpen(true);
            }}
          />
        ) : null}
        {sheetOpen && (
          <Suspense fallback={<KeyboardHoldFallback />}>
            <WorkAreaPickerSheet
              onClose={() => setSheetOpen(false)}
              groups={switcherGroups}
              order={sharedSwitcherOrder()}
              recentIds={projectStore.recentWorkAreaIds}
              unavailable={unavailableDataSources}
              selectedProjectId={selectedProjectId}
              overviewMode={overviewMode}
              loading={!projectStore.hasWorkAreaAnswer && projectStore.loading}
              failure={
                !projectStore.hasWorkAreaAnswer && !projectStore.loading ? projectStore.error : null
              }
              onRetry={() => void projectStore.fetchProjects()}
              createDisabledReason={createDisabledReason}
              onSelect={selectProject}
              onSelectNamespace={(namespaceId) => {
                setSheetOpen(false);
                onSelectNamespace(namespaceId);
              }}
              onOverview={() => {
                setSheetOpen(false);
                onWorkAreaOverview();
              }}
              onCreate={() => {
                setSheetOpen(false);
                onCreateProject();
              }}
              renderEditor={renderPhoneEditor}
            />
          </Suspense>
        )}
        {/* The row has no room for an error box; the sheet it opens says what failed. */}
        {topRow ? null : (loadFailure ?? loadSlow)}
      </PhoneSelectorFrame>
    );
  }

  return (
    <section aria-label={t('navigation.sidebar.workAreas')} className="mb-3 space-y-1.5">
      {guestLogo}
      {/*
        I-1304 (IL-17): a row of their own above the picker, which keeps the sidebar's full width:
        before it, two 44 px targets left the work area's name 75 px ("acm…").
      */}
      {leading ? <div className="flex">{leading}</div> : null}
      {!expanded ? (
        <div
          ref={dropdownRowRef}
          data-work-area-picker-row=""
          className="flex items-center gap-1.5"
        >
          <div ref={dropdownRef} className="relative min-w-0 flex-1">
            <button
              ref={dropdownTriggerRef}
              type="button"
              aria-label={
                guestSelected && selectedProject
                  ? guestAgency
                    ? t('navigation.sidebar.guestSelected', { label: guestAgency })
                    : t('navigation.sidebar.guestPages')
                  : t('navigation.sidebar.selectedWorkArea')
              }
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              disabled={workAreaSelectorDisabled(selectorState)}
              className="flex h-8 w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-[#FEFEFE]/80 px-2.5 text-left text-[13px] font-medium text-gray-700 transition-colors duration-150 hover:bg-[#D7DBDC]/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:text-gray-400 dark:bg-[#212121]/80 dark:text-gray-200 dark:hover:bg-[#303030] dark:disabled:text-gray-500"
              // T-0968: the open list is its own chunk; a mouse's hover or a focus reads it before the
              // click (a finger's tap reads it on open, and Layout reads it after the launch anyway).
              onPointerEnter={onHoverPointer(WorkAreaPickerOptions.preload)}
              onFocus={WorkAreaPickerOptions.preload}
              onClick={() => setDropdownOpen((open) => !open)}
            >
              {/*
                I-1677 (IP-33): with a finger the label, which is the qualified name when two work areas
                share one ("Shell Check Organisation / iOS shell check"), takes the trigger's two lines
                and is cut only at a word, on the second. One CSS-cut line drew "Shell Check Organis…"
                on the iPad with a line to spare. The desktop keeps one line (IP-32).
              */}
              {touchSidebar ? (
                <WordTruncate
                  text={workAreaSelectorLabel(selectorState, t)}
                  lines={2}
                  className="flex-1 leading-4"
                />
              ) : (
                <span className="min-w-0 flex-1 truncate">
                  {workAreaSelectorLabel(selectorState, t)}
                </span>
              )}
              {/* F-0407 SB-4: the open-item count is gone from the selection, in every form it took. */}
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
            </button>

            {dropdownOpen && (
              <AnchoredPortal>
                <div
                  ref={dropdownPanelRef}
                  style={dropdownStyle}
                  className="z-[60] overflow-y-auto rounded-md bg-[#F0F1F2] p-1 shadow-lg ring-1 ring-black/5 dark:bg-[#242424] dark:ring-white/10"
                >
                  {workAreaOptionCount > 0 ? (
                    // T-0629 (AC-19.18.4): in the order the person's switcher setting names.
                    <WorkAreaPickerOptions
                      groups={switcherGroups}
                      unavailable={unavailableDataSources}
                      order={sharedSwitcherOrder()}
                      recentIds={projectStore.recentWorkAreaIds}
                      selectedProjectId={selectedProjectId}
                      overviewMode={overviewMode}
                      inNamespaceScope={inNamespaceScope}
                      onOverview={() => {
                        setDropdownOpen(false);
                        onWorkAreaOverview();
                      }}
                      onSelectNamespace={(namespaceId) => {
                        setDropdownOpen(false);
                        onSelectNamespace(namespaceId);
                      }}
                      onSelect={selectProject}
                      // I-1677 (IP-33): with a finger the open list wraps as its trigger does.
                      twoLines={touchSidebar}
                    />
                  ) : null}
                  {/*
                  Creating one used to be reachable only from the expanded list, so a reader in the
                  collapsed sidebar had to expand, create and collapse again — and a reader with
                  none at all had no way in from here at all. It is an action rather than one more
                  thing to pick, so it sits outside the listbox, under a rule, and stays put while
                  the list above it scrolls.
                */}
                  <button
                    type="button"
                    {...gateControlProps(createGate, { otherwiseDisabled: projectStore.loading })}
                    {...dropdownCreateRefusal.controlProps}
                    onClick={() => {
                      setDropdownOpen(false);
                      onCreateProject();
                    }}
                    className={`flex h-8 w-full cursor-pointer items-center gap-2 rounded-md px-2 text-left text-[13px] font-medium text-gray-600 transition-colors duration-150 ${createHover} focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:text-gray-400 dark:text-gray-300 dark:disabled:text-gray-500 ${
                      workAreaOptionCount > 0
                        ? 'mt-1 border-t border-black/5 pt-1 dark:border-white/10'
                        : ''
                    }`}
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                    <span>{t('navigation.sidebar.newWorkArea')}</span>
                  </button>
                  <RefusalDescription
                    id={dropdownCreateRefusal.id}
                    reason={dropdownCreateRefusal.reason}
                  />
                </div>
              </AnchoredPortal>
            )}
          </div>
          <button
            type="button"
            aria-label={t('navigation.sidebar.expandWorkAreas')}
            title={t('navigation.sidebar.expandWorkAreas')}
            aria-expanded={false}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-gray-500 transition-colors duration-150 hover:bg-[#D7DBDC]/50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-400 dark:hover:bg-[#303030] dark:hover:text-gray-100"
            onClick={() => setSessionExpanded(true)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          {onClose ? (
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('navigation.sidebar.closeNavigation')}
              title={t('navigation.sidebar.closeNavigation')}
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-1 rounded-md bg-[#F0F1F2]/70 p-1 dark:bg-[#242424]">
          {/*
            I-1308 (CFC-f6) drew the heading on a line of its own with a coarse pointer and its 44 px
            icons right-aligned on the next, which read as three loose icons under a title (U-0024,
            Jay's iPad screenshot). U-0024 (IP-27): one line, as the desktop draws it. Three 44 px
            icons 8 px apart left the heading's 80 px of words 64, so on the iPad Create work area
            and New organisation are one ＋ whose menu offers both, and the line holds the heading,
            the ＋ and the fold.

            F-0585 (PSW-7, QA 0520): at the sidebar's narrowest, 236, the heading's 80 px of words
            and its 8 px of padding were 4 px more than the line left it, so with a finger it read
            "WORK ARE…" (88 in 84). Its padding is 2 px on the left and none on the right there.
          */}
          <div
            data-work-areas-header=""
            className="flex h-7 items-center gap-2 px-2 [@media(pointer:coarse)]:h-auto"
          >
            <button
              type="button"
              onClick={onWorkAreaOverview}
              className={`min-w-0 flex-1 truncate rounded px-1 text-left text-[12px] font-semibold uppercase outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus [@media(pointer:coarse)]:pl-0.5 [@media(pointer:coarse)]:pr-0 ${
                overviewMode
                  ? 'text-gray-950 dark:text-white'
                  : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              {t('navigation.sidebar.workAreas')}
            </button>
            {touchSidebar && presentation === 'sidebar' ? (
              <SidebarActionsMenu
                label={t('navigation.sidebar.newWorkAreaOrOrganisation')}
                menuLabel={t('navigation.sidebar.new')}
                icon={<Plus aria-hidden="true" className="h-4 w-4" />}
                entries={[
                  {
                    label: t('navigation.sidebar.newWorkArea'),
                    title: t('navigation.sidebar.createWorkArea'),
                    onSelect: () => onCreateProject(),
                    gate: createGate,
                    disabled: projectStore.loading,
                  },
                  ...(namespaceStore
                    ? [
                        {
                          label: t('navigation.sidebar.newOrganisationEllipsis'),
                          title: t('navigation.sidebar.newOrganisationOpens'),
                          onSelect: () => {
                            onClose?.();
                            navigate(ORGANISATION_FLOW_PATH);
                          },
                          gate: organisationGate,
                        },
                      ]
                    : []),
                ]}
              />
            ) : (
              <>
                <button
                  type="button"
                  aria-label={t('navigation.sidebar.createWorkArea')}
                  {...gateControlProps(createGate, {
                    otherwiseDisabled: projectStore.loading,
                    title: t('navigation.sidebar.createWorkArea'),
                  })}
                  {...headerCreateRefusal.controlProps}
                  className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-gray-500 transition-colors duration-150 ${createHover} focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:text-gray-400 dark:text-gray-400 dark:disabled:text-gray-500`}
                  onClick={() => onCreateProject()}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
                <RefusalDescription
                  id={headerCreateRefusal.id}
                  reason={headerCreateRefusal.reason}
                />
                {namespaceStore ? (
                  /*
                I-0682, AC-O2: a link, not a create control. Jay, 2026-09-24: creating an
                organisation is "a more sophisticated process only be startable from the global
                settings page". The flow explains what an organisation is and what this deployment
                allows before anything is claimed.
              */
                  organisationRefused ? (
                    <button
                      type="button"
                      aria-label={t('navigation.sidebar.newOrganisationOpensLabel')}
                      data-testid="sidebar-organisation-flow-link"
                      {...gateControlProps(organisationGate)}
                      className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-gray-500 transition-colors duration-150 ${createHover} focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-400`}
                    >
                      <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ) : (
                    <Link
                      to={ORGANISATION_FLOW_PATH}
                      aria-label={t('navigation.sidebar.newOrganisationOpensLabel')}
                      title={t('navigation.sidebar.newOrganisationOpens')}
                      data-testid="sidebar-organisation-flow-link"
                      onClick={onClose}
                      className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-gray-500 transition-colors duration-150 hover:bg-[#E4E6E8] hover:text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-400 dark:hover:bg-[#1f1f1f] dark:hover:text-white"
                    >
                      <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  )
                ) : null}
              </>
            )}
            <button
              type="button"
              aria-label={t('navigation.sidebar.collapseWorkAreas')}
              title={t('navigation.sidebar.collapseWorkAreas')}
              aria-expanded={true}
              className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-gray-500 transition-colors duration-150 hover:bg-[#E4E6E8] hover:text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-gray-400 dark:hover:bg-[#1f1f1f] dark:hover:text-white"
              onClick={() => setSessionExpanded(false)}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            {onClose ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('navigation.sidebar.closeNavigation')}
                title={t('navigation.sidebar.closeNavigation')}
                className="h-6 w-6 shrink-0 px-0"
                onClick={onClose}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>

          {tree(false)}
        </div>
      )}
      {loadFailure ?? loadSlow}
    </section>
  );
});

interface SortableWorkAreaRowProps {
  row: WorkAreaHierarchyRow;
  /** The row's own namespace's depth among organisations/subgroups — `workAreaIndent.ts`'s other half. */
  groupDepth: number;
  selected: boolean;
  reorderDisabled: boolean;
  touchHandleVisible: boolean;
  onTouchReveal: (projectId: string) => void;
  onSelect: (projectId: string) => void;
  /** F-0416: the phone picker's edit mode — a 44 px row with its handle beside it, for a finger. */
  comfortable?: boolean;
  /** I-1263 (AC-18.11): the sibling a move arrow swaps with, or `null` at that end. */
  moveUpTo: string | null;
  moveDownTo: string | null;
  onMove: (overId: string | null) => void;
}

function SortableWorkAreaRow({
  row: { project, depth: rowDepth },
  groupDepth,
  selected,
  reorderDisabled,
  touchHandleVisible,
  onTouchReveal,
  onSelect,
  comfortable = false,
  moveUpTo,
  moveDownTo,
  onMove,
}: SortableWorkAreaRowProps) {
  const t = useT();
  // F-0380's rule, as an organisation's heading has it: a finger gets the arrows at any width.
  const touchOnly = useMediaQuery(TOUCH_ONLY_MEDIA_QUERY);
  /*
    U-0024 (IP-28): on the iPad's sidebar the handle, hidden until the row is tapped, took a 44 px
    column of its own and drew every name 36 px right of the desktop's. Until it shows, its place
    is the desktop's 24 px, so the name stands where the desktop draws it.
  */
  const touchSidebar = useTouchDesktopLayout() && !comfortable;
  const { attributes, listeners, setNodeRef, transformCss, transition, isDragging } =
    useRowSortable({ id: project.id, disabled: reorderDisabled });
  const handleSpacer = touchSidebar && !touchHandleVisible && !isDragging;
  // Every work area sits under some namespace (never depth 0 on its own), so this is always true —
  // kept as its own value rather than a bare `true` literal, matching what it means.
  const nested = rowDepth + groupDepth + 1 > 0;
  // I-1074: the handle shows after the tap, not while the finger is down (`useTouchReveal`).
  const touchReveal = useTouchReveal(() => onTouchReveal(project.id));

  return (
    <div
      ref={setNodeRef}
      data-work-area-row=""
      // F-0407 SB-6: a 44px touch target on a coarse pointer, the same pattern
      // `PageNavigationList.tsx`'s own drag handle already uses — row, handle and select button
      // all grow together so nothing inside overflows a taller row.
      //
      // I-1074: `hover:` here used to apply unconditionally, on every pointer type. WebKit treats
      // a tap on (or inside) an element that carries live `:hover` styling as "simulate hover
      // first" — the row's fill changed to its hover colour on the first touch, reading as
      // "highlighted", and only a second tap reached the select button's own `onClick` below.
      // `[@media(hover:hover)]:` is the same guard `ConfiguredViewRuntime.tsx`'s row actions
      // already use for the same reason: a device with no real hover (a touch screen) never
      // matches it, so no `:hover` rule exists there for WebKit to simulate, and the first tap
      // reaches the button directly.
      className={`group flex w-full items-center rounded-md pr-2 transition-colors duration-150 ${
        comfortable
          ? 'min-h-touch text-[15px]'
          : 'h-7 text-[13px] [@media(pointer:coarse)]:min-h-touch'
      } ${
        selected
          ? 'bg-[#DDE0E2] font-semibold text-gray-950 dark:bg-[#303030] dark:text-white'
          : 'text-gray-600 dark:text-gray-300 [@media(hover:hover)]:hover:bg-[#E4E6E8] [@media(hover:hover)]:hover:text-gray-950 [@media(hover:hover)]:dark:hover:bg-[#1f1f1f] [@media(hover:hover)]:dark:hover:text-white'
      } ${isDragging ? 'relative z-10 opacity-70 shadow-md' : ''}`}
      style={{
        paddingLeft: workAreaRowIndent(groupDepth, rowDepth),
        transform: transformCss,
        transition,
      }}
      onPointerDown={touchReveal.onPointerDown}
      onClick={touchReveal.onClick}
    >
      {handleSpacer ? (
        <span aria-hidden="true" data-work-area-handle-place="" className="w-6 shrink-0" />
      ) : (
        <button
          type="button"
          aria-label={t('navigation.sidebar.reorder', { name: workAreaDisplayName(project) })}
          title={t('navigation.sidebar.dragToReorder')}
          disabled={reorderDisabled}
          className={`work-area-drag-handle grid shrink-0 cursor-grab place-items-center rounded text-gray-400 outline-none ${
            comfortable
              ? 'h-touch w-touch'
              : 'h-7 w-6 [@media(pointer:coarse)]:h-touch [@media(pointer:coarse)]:w-touch'
          } hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 active:cursor-grabbing disabled:cursor-default disabled:opacity-35 dark:text-gray-500 dark:hover:text-gray-200 ${
            touchHandleVisible || isDragging ? 'work-area-drag-handle--visible' : ''
          }`}
          {...attributes}
          {...listeners}
        >
          <GripVertical
            className={comfortable ? 'h-4 w-4 touch-glyph' : 'h-3.5 w-3.5 touch-glyph'}
          />
        </button>
      )}
      {nested ? (
        <span aria-hidden="true" className="mr-2 h-3 w-px shrink-0 bg-gray-300 dark:bg-gray-600" />
      ) : null}
      <button
        type="button"
        onClick={() => onSelect(project.id)}
        className={`flex min-w-0 flex-1 items-center gap-2 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
          comfortable ? 'min-h-touch' : 'h-7 [@media(pointer:coarse)]:min-h-touch'
        }`}
      >
        <WordTruncate
          text={workAreaDisplayName(project)}
          title={workAreaQualifiedLabel(project)}
          className="flex-1"
        />
        {project.visibility === 'private' ? (
          <LockKeyhole
            className="h-3 w-3 shrink-0 text-gray-400"
            aria-label={t('navigation.sidebar.isPrivate', { name: workAreaDisplayName(project) })}
          />
        ) : null}
      </button>
      {/* I-1263 (AC-18.11): the edit mode's arrow column, shared with the organisations' headings.
          The narrow sidebar keeps the drag handle alone, which leaves the name its room. */}
      {comfortable ? (
        <MoveArrows
          name={workAreaDisplayName(project)}
          touchOnly={touchOnly}
          disabled={reorderDisabled}
          hasRoomAbove={moveUpTo !== null}
          hasRoomBelow={moveDownTo !== null}
          onMove={(direction) => onMove(direction === 'up' ? moveUpTo : moveDownTo)}
          // The row ends 8 px in (`pr-2`), the namespace heading 4 px (`pr-1`): one column for both.
          className="-mr-1"
        />
      ) : null}
    </div>
  );
}

/**
 * The count on *Decisions for me* (I-0480). Zero renders nothing rather than "0": on a to-do entry
 * an empty badge is the good news, and a zero is one more thing to read. While the first read is in
 * flight it shows `...`, as the needs-work badge does, so the entry never claims a number it does
 * not have.
 */
export function DecisionsWaitingBadge({
  count,
  failed = false,
}: {
  count: number | undefined;
  /** The read failed: say nothing, rather than `...` for a number that is never coming. */
  failed?: boolean;
}) {
  const t = useT();
  const format = useFormat();
  if (count === 0 || (failed && count === undefined)) return null;
  const countLabel = count === undefined ? '...' : format.number(count);
  return (
    <span
      data-testid="decisions-waiting-badge"
      // F-0579 ACC-5: a count's number takes the highlight in Colour.
      data-accent-count=""
      className="shrink-0 rounded bg-[#DDE0E2] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-gray-600 dark:bg-[#303030] dark:text-gray-300"
      title={
        count === undefined
          ? t('navigation.sidebar.loadingDecisions')
          : t('navigation.sidebar.decisionsWaitingTitle', { count: countLabel })
      }
      aria-label={
        count === undefined
          ? t('navigation.sidebar.loadingDecisions')
          : t('navigation.sidebar.decisionsWaiting', { count })
      }
    >
      {countLabel}
    </span>
  );
}

/**
 * The count on *Tasks for me* (I-0900): the same rules as `DecisionsWaitingBadge` — zero renders
 * nothing, `...` while the first read is in flight, nothing at all when the read failed.
 */
export function TasksWaitingBadge({
  count,
  urgent,
  failed = false,
}: {
  count: number | undefined;
  /** I-2602 (TF-38): how many of them are urgent; nothing is said when none is. */
  urgent?: number;
  failed?: boolean;
}) {
  const t = useT();
  const format = useFormat();
  if (count === 0 || (failed && count === undefined)) return null;
  const countLabel = count === undefined ? '...' : format.number(count);
  const urgentPart = count !== undefined && urgent ? urgent : 0;
  const name =
    count === undefined
      ? t('navigation.sidebar.loadingTasks')
      : urgentPart > 0
        ? t('navigation.sidebar.tasksWaitingWithUrgent', {
            count,
            urgent: format.number(urgentPart),
          })
        : t('navigation.sidebar.tasksWaiting', { count });
  return (
    <span className="flex shrink-0 items-center gap-1" aria-label={name} role="img">
      <span
        data-testid="tasks-waiting-badge"
        // F-0579 ACC-5: a count's number takes the highlight in Colour.
        data-accent-count=""
        className="shrink-0 rounded bg-[#DDE0E2] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-gray-600 dark:bg-[#303030] dark:text-gray-300"
        title={
          count === undefined
            ? t('navigation.sidebar.loadingTasks')
            : urgentPart > 0
              ? t('navigation.sidebar.tasksWaitingTitleWithUrgent', {
                  count: countLabel,
                  urgent: format.number(urgentPart),
                })
              : t('navigation.sidebar.tasksWaitingTitle', { count: countLabel })
        }
        aria-hidden="true"
      >
        {countLabel}
      </span>
      {urgentPart > 0 ? (
        // TF-38: the urgent part in words beside the count, never a colour alone.
        <span
          data-testid="tasks-urgent-badge"
          className="shrink-0 rounded bg-[#FDE8E8] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-[#9B1C1C] dark:bg-[#4A1D1D] dark:text-[#FCA5A5]"
          aria-hidden="true"
        >
          {t('navigation.sidebar.tasksUrgent', { count: format.number(urgentPart) })}
        </span>
      ) : null}
    </span>
  );
}
